import React, { useState, useEffect, useCallback, useMemo, lazy, Suspense } from 'react';
import axios from 'axios';
import { useDebounce } from '../hooks/useDebounce';
import { Plus, Edit2, Trash2, Search, Upload, Download, Pill, Activity, X, FileText, ChevronDown, ChevronRight, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import SearchableSelect from './SearchableSelect';
import DoctorLeaveManagement from './DoctorLeaveManagement';
import MedicationIcon from './MedicationIcon';
import { toast } from 'sonner';

// Loading spinner for dialogs
const DialogLoading = () => (
  <div className="flex items-center justify-center py-8">
    <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
    <span className="ml-2 text-gray-600">Loading...</span>
  </div>
);

const API = process.env.REACT_APP_BACKEND_URL;

const ConfigurationTab = ({ user }) => {
  // State for templates
  const [medicationTemplates, setMedicationTemplates] = useState([]);
  const [chiefComplaintTemplates, setChiefComplaintTemplates] = useState([]);
  const [testTemplates, setTestTemplates] = useState([]);
  const [diagnosisTemplates, setDiagnosisTemplates] = useState([]);
  const [additionalNotesTemplates, setAdditionalNotesTemplates] = useState([]);
  const [commonMedications, setCommonMedications] = useState([]);
  const [doctorSettings, setDoctorSettings] = useState({ show_common_medications: true });
  
  // Search/filter state
  const [medicationSearch, setMedicationSearch] = useState('');
  const [chiefComplaintSearch, setChiefComplaintSearch] = useState('');
  const [testSearch, setTestSearch] = useState('');
  const [diagnosisSearch, setDiagnosisSearch] = useState('');
  
  // Debounced search values for performance
  const debouncedMedicationSearch = useDebounce(medicationSearch, 300);
  const debouncedDiagnosisSearch = useDebounce(diagnosisSearch, 300);
  
  // Collapsible state for test templates
  const [expandedTestTypes, setExpandedTestTypes] = useState({});
  const [expandedTestCategories, setExpandedTestCategories] = useState({});
  const [additionalNotesSearch, setAdditionalNotesSearch] = useState('');
  const [commonMedSearch, setCommonMedSearch] = useState('');
  
  // Dialog states
  const [showAddMedication, setShowAddMedication] = useState(false);
  const [showEditMedication, setShowEditMedication] = useState(false);
  const [showAddChiefComplaint, setShowAddChiefComplaint] = useState(false);
  const [showEditChiefComplaint, setShowEditChiefComplaint] = useState(false);
  const [showAddTest, setShowAddTest] = useState(false);
  const [showEditTest, setShowEditTest] = useState(false);
  const [showAddDiagnosis, setShowAddDiagnosis] = useState(false);
  const [showEditDiagnosis, setShowEditDiagnosis] = useState(false);
  const [showAddAdditionalNotes, setShowAddAdditionalNotes] = useState(false);
  const [showEditAdditionalNotes, setShowEditAdditionalNotes] = useState(false);
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [showCommonMedUpload, setShowCommonMedUpload] = useState(false);
  const [showTemplateCSVUpload, setShowTemplateCSVUpload] = useState(false);
  
  // Editing states
  const [editingMedication, setEditingMedication] = useState(null);
  const [editingChiefComplaint, setEditingChiefComplaint] = useState(null);
  const [editingTest, setEditingTest] = useState(null);
  const [editingDiagnosis, setEditingDiagnosis] = useState(null);
  const [editingAdditionalNotes, setEditingAdditionalNotes] = useState(null);
  
  // Form states
  const [medicationForm, setMedicationForm] = useState({
    medication_name: '',
    medication_type: '',
    generic_name: '',
    manufacturer: '',
    treatment_for: '',
    category: '',
    time_of_day: [],
    frequency: '',
    timing: 'Before Food',
    duration: '',
    notes: ''
  });
  
  const [chiefComplaintForm, setChiefComplaintForm] = useState({
    complaint_name: '',
    description: '',
    sub_complaints: [],
    associated_medications: [],
    associated_tests: [],
    medicationSearch: '',
    testSearch: ''
  });
  
  // State for adding new sub-complaint
  const [newSubComplaint, setNewSubComplaint] = useState('');
  const [editingSubComplaintIndex, setEditingSubComplaintIndex] = useState(null);
  const [showSubComplaintMedications, setShowSubComplaintMedications] = useState(false);
  
  // State for sub-sub complaints (Level 3)
  const [newSubSubComplaint, setNewSubSubComplaint] = useState('');
  const [editingSubSubComplaintIndex, setEditingSubSubComplaintIndex] = useState(null);
  const [showSubSubComplaintMedications, setShowSubSubComplaintMedications] = useState(false);
  const [managingSubSubForIndex, setManagingSubSubForIndex] = useState(null);
  
  // State for Level 4 complaints
  const [newLevel4Complaint, setNewLevel4Complaint] = useState('');
  const [editingLevel4Index, setEditingLevel4Index] = useState(null);
  const [showLevel4Medications, setShowLevel4Medications] = useState(false);
  const [managingLevel4ForIndex, setManagingLevel4ForIndex] = useState(null);
  const [managingLevel4ForSubIndex, setManagingLevel4ForSubIndex] = useState(null);
  
  // State for Level 5 complaints
  const [newLevel5Complaint, setNewLevel5Complaint] = useState('');
  const [editingLevel5Index, setEditingLevel5Index] = useState(null);
  const [showLevel5Medications, setShowLevel5Medications] = useState(false);
  const [managingLevel5ForIndex, setManagingLevel5ForIndex] = useState(null);
  
  // State for Level 6 complaints
  const [newLevel6Complaint, setNewLevel6Complaint] = useState('');
  const [editingLevel6Index, setEditingLevel6Index] = useState(null);
  const [showLevel6Medications, setShowLevel6Medications] = useState(false);
  const [managingLevel6ForIndex, setManagingLevel6ForIndex] = useState(null);
  
  // State for Diagnosis Level 2
  const [newLevel2Diagnosis, setNewLevel2Diagnosis] = useState('');
  const [editingLevel2DiagnosisIndex, setEditingLevel2DiagnosisIndex] = useState(null);
  const [showLevel2DiagnosisNotes, setShowLevel2DiagnosisNotes] = useState(false);
  
  // State for Diagnosis Level 3
  const [managingLevel3ForDiagnosisIndex, setManagingLevel3ForDiagnosisIndex] = useState(null);
  const [newLevel3Diagnosis, setNewLevel3Diagnosis] = useState('');
  const [editingLevel3DiagnosisIndex, setEditingLevel3DiagnosisIndex] = useState(null);
  const [showLevel3DiagnosisNotes, setShowLevel3DiagnosisNotes] = useState(false);
  const [testForm, setTestForm] = useState({
    test_name: '',
    test_type: 'Blood Test',
    category: '',
    description: ''
  });

  const [diagnosisForm, setDiagnosisForm] = useState({
    diagnosis_name: '',
    description: '',
    associated_notes: [],
    level_2_diagnosis: [],
    notesTemplateSearch: ''
  });

  const [additionalNotesForm, setAdditionalNotesForm] = useState({
    note_title: '',
    note_content: ''
  });
  
  // Bulk import state
  const [bulkImportText, setBulkImportText] = useState('');
  const [csvFile, setCsvFile] = useState(null);
  const [uploadMethod, setUploadMethod] = useState('paste'); // 'paste' or 'csv'

  // Load templates on mount
  useEffect(() => {
    if (user?.id) {
      loadMedicationTemplates();
      loadChiefComplaintTemplates();
      loadTestTemplates();
      loadDiagnosisTemplates();
      loadAdditionalNotesTemplates();
      loadCommonMedications();
      loadDoctorSettings();
    }
  }, [user]);

  const loadMedicationTemplates = async () => {
    try {
      const response = await axios.get(`${API}/api/medication-templates/${user.id}`);
      setMedicationTemplates(response.data);
    } catch (error) {
      console.error('Error loading medication templates:', error);
    }
  };

  const loadChiefComplaintTemplates = async () => {
    try {
      const response = await axios.get(`${API}/api/chief-complaint-templates/${user.id}`);
      setChiefComplaintTemplates(response.data);
    } catch (error) {
      console.error('Error loading chief complaint templates:', error);
    }
  };

  const loadTestTemplates = async () => {
    try {
      const response = await axios.get(`${API}/api/test-templates/${user.id}`);
      setTestTemplates(response.data);
    } catch (error) {
      console.error('Error loading test templates:', error);
    }
  };

  const loadDiagnosisTemplates = async () => {
    try {
      const response = await axios.get(`${API}/api/diagnosis-templates/${user.id}`);
      setDiagnosisTemplates(response.data);
    } catch (error) {
      console.error('Error loading diagnosis templates:', error);
    }
  };

  const loadAdditionalNotesTemplates = async () => {
    try {
      const response = await axios.get(`${API}/api/additional-notes-templates/${user.id}`);
      setAdditionalNotesTemplates(response.data);
    } catch (error) {
      console.error('Error loading additional notes templates:', error);
    }
  };

  const loadCommonMedications = async () => {
    try {
      const response = await axios.get(`${API}/api/common-medications`);
      setCommonMedications(response.data);
    } catch (error) {
      console.error('Error loading common medications:', error);
    }
  };

  const loadDoctorSettings = async () => {
    try {
      const response = await axios.get(`${API}/api/doctor-settings/${user.id}`);
      setDoctorSettings(response.data);
    } catch (error) {
      console.error('Error loading doctor settings:', error);
    }
  };

  // Medication CRUD
  const handleAddMedication = async (e) => {
    e.preventDefault();
    if (!medicationForm.medication_name) {
      toast.error('Please enter medication name');
      return;
    }

    try {
      await axios.post(`${API}/api/medication-templates`, {
        doctor_id: user.id,
        ...medicationForm
      });
      toast.success('Medication template added successfully');
      setShowAddMedication(false);
      setMedicationForm({ 
        medication_name: '', 
        medication_type: '', 
        generic_name: '', 
        manufacturer: '',
        category: '',
        treatment_for: '', 
        time_of_day: [], 
        frequency: '', 
        timing: 'Before Food', 
        duration: '', 
        notes: '' 
      });
      loadMedicationTemplates();
    } catch (error) {
      console.error('Error adding medication:', error);
      toast.error('Failed to add medication template');
    }
  };

  const handleEditMedication = async (e) => {
    e.preventDefault();
    if (!editingMedication) return;

    try {
      await axios.put(`${API}/api/medication-templates/${editingMedication.id}`, {
        doctor_id: user.id,
        ...medicationForm
      });
      toast.success('Medication template updated successfully');
      setShowEditMedication(false);
      setEditingMedication(null);
      loadMedicationTemplates();
    } catch (error) {
      console.error('Error updating medication:', error);
      toast.error('Failed to update medication template');
    }
  };

  const handleDeleteMedication = async (id) => {
    if (!window.confirm('Are you sure you want to delete this medication template?')) return;

    try {
      await axios.delete(`${API}/api/medication-templates/${id}`);
      toast.success('Medication template deleted successfully');
      loadMedicationTemplates();
    } catch (error) {
      console.error('Error deleting medication:', error);
      toast.error('Failed to delete medication template');
    }
  };

  const openEditMedication = (medication) => {
    setEditingMedication(medication);
    setMedicationForm({
      medication_name: medication.medication_name,
      medication_type: medication.medication_type || '',
      generic_name: medication.generic_name || '',
      treatment_for: medication.treatment_for || '',
      time_of_day: medication.time_of_day || [],
      frequency: medication.frequency || '',
      timing: medication.timing,
      duration: medication.duration,
      notes: medication.notes || ''
    });
    setShowEditMedication(true);
  };

  // Chief Complaint Template Functions
  const handleAddChiefComplaint = async (e) => {
    e.preventDefault();
    
    try {
      const { medicationSearch, testSearch, ...apiData } = chiefComplaintForm;
      
      await axios.post(`${API}/api/chief-complaint-templates`, {
        doctor_id: user.id,
        ...apiData
      });
      toast.success('Chief complaint template added successfully');
      setChiefComplaintForm({ 
        complaint_name: '', 
        description: '', 
        sub_complaints: [],
        associated_medications: [], 
        associated_tests: [],
        medicationSearch: '',
        testSearch: ''
      });
      setNewSubComplaint('');
      setShowAddChiefComplaint(false);
      loadChiefComplaintTemplates();
    } catch (error) {
      console.error('Error adding chief complaint:', error);
      toast.error('Failed to add chief complaint template');
    }
  };

  const handleEditChiefComplaint = async (e) => {
    e.preventDefault();
    
    try {
      const { medicationSearch, testSearch, ...apiData } = chiefComplaintForm;
      
      await axios.put(`${API}/api/chief-complaint-templates/${editingChiefComplaint.id}`, {
        doctor_id: user.id,
        ...apiData
      });
      toast.success('Chief complaint template updated successfully');
      setChiefComplaintForm({ 
        complaint_name: '', 
        description: '', 
        sub_complaints: [],
        associated_medications: [], 
        associated_tests: [],
        medicationSearch: '',
        testSearch: ''
      });
      setShowEditChiefComplaint(false);
      setEditingChiefComplaint(null);
      loadChiefComplaintTemplates();
    } catch (error) {
      console.error('Error updating chief complaint:', error);
      toast.error('Failed to update chief complaint template');
    }
  };

  const handleDeleteChiefComplaint = async (id) => {
    if (!window.confirm('Are you sure you want to delete this chief complaint template?')) return;

    try {
      await axios.delete(`${API}/api/chief-complaint-templates/${id}`);
      toast.success('Chief complaint template deleted successfully');
      loadChiefComplaintTemplates();
    } catch (error) {
      console.error('Error deleting chief complaint:', error);
      toast.error('Failed to delete chief complaint template');
    }
  };

  const openEditChiefComplaint = (complaint) => {
    setEditingChiefComplaint(complaint);
    setChiefComplaintForm({
      complaint_name: complaint.complaint_name,
      description: complaint.description || '',
      sub_complaints: complaint.sub_complaints || [],
      associated_medications: complaint.associated_medications || [],
      associated_tests: complaint.associated_tests || [],
      medicationSearch: '',
      testSearch: ''
    });
    setShowEditChiefComplaint(true);
  };

  // Test Template Functions
  const handleAddTest = async (e) => {
    e.preventDefault();
    
    // Validate category for Blood Test
    if (testForm.test_type === 'Blood Test' && !testForm.category) {
      toast.error('Please select a category for Blood Test');
      return;
    }
    
    try {
      await axios.post(`${API}/api/test-templates`, {
        doctor_id: user.id,
        test_name: testForm.test_name,
        description: testForm.test_type === 'Blood Test' 
          ? `${testForm.test_type} - ${testForm.category}${testForm.description ? ' - ' + testForm.description : ''}`
          : `${testForm.test_type}${testForm.description ? ' - ' + testForm.description : ''}`
      });
      toast.success('Test template added successfully');
      setTestForm({ test_name: '', test_type: 'Blood Test', category: '', description: '' });
      setShowAddTest(false);
      loadTestTemplates();
    } catch (error) {
      console.error('Error adding test:', error);
      toast.error('Failed to add test template');
    }
  };

  const handleEditTest = async (e) => {
    e.preventDefault();
    
    // Validate category for Blood Test
    if (testForm.test_type === 'Blood Test' && !testForm.category) {
      toast.error('Please select a category for Blood Test');
      return;
    }
    
    try {
      await axios.put(`${API}/api/test-templates/${editingTest.id}`, {
        doctor_id: user.id,
        test_name: testForm.test_name,
        description: testForm.test_type === 'Blood Test' 
          ? `${testForm.test_type} - ${testForm.category}${testForm.description ? ' - ' + testForm.description : ''}`
          : `${testForm.test_type}${testForm.description ? ' - ' + testForm.description : ''}`
      });
      toast.success('Test template updated successfully');
      setTestForm({ test_name: '', test_type: 'Blood Test', category: '', description: '' });
      setShowEditTest(false);
      setEditingTest(null);
      loadTestTemplates();
    } catch (error) {
      console.error('Error updating test:', error);
      toast.error('Failed to update test template');
    }
  };

  const handleDeleteTest = async (id) => {
    if (!window.confirm('Are you sure you want to delete this test template?')) return;

    try {
      await axios.delete(`${API}/api/test-templates/${id}`);
      toast.success('Test template deleted successfully');
      loadTestTemplates();
    } catch (error) {
      console.error('Error deleting test:', error);
      toast.error('Failed to delete test template');
    }
  };

  const openEditTest = (test) => {
    // Don't allow editing common tests
    if (test.doctor_id === 'common') {
      toast.error('Cannot edit default test templates');
      return;
    }
    
    setEditingTest(test);
    
    // Parse description to extract test_type, category, and description
    const desc = test.description || '';
    const parts = desc.split(' - ');
    const test_type = parts[0] || 'Other';
    const category = test_type === 'Blood Test' && parts[1] ? parts[1] : '';
    const description = test_type === 'Blood Test' && parts[2] ? parts[2] : (parts[1] || '');
    
    setTestForm({
      test_name: test.test_name,
      test_type: test_type,
      category: category,
      description: description
    });
    setShowEditTest(true);
  };

  // Diagnosis Template Functions
  const handleAddDiagnosis = async (e) => {
    e.preventDefault();
    
    try {
      await axios.post(`${API}/api/diagnosis-templates`, {
        doctor_id: user.id,
        ...diagnosisForm
      });
      toast.success('Diagnosis template added successfully');
      setDiagnosisForm({ diagnosis_name: '', description: '', associated_notes: [], level_2_diagnosis: [], notesTemplateSearch: '' });
      setShowAddDiagnosis(false);
      loadDiagnosisTemplates();
    } catch (error) {
      console.error('Error adding diagnosis:', error);
      toast.error('Failed to add diagnosis template');
    }
  };

  const handleEditDiagnosis = async (e) => {
    e.preventDefault();
    
    try {
      await axios.put(`${API}/api/diagnosis-templates/${editingDiagnosis.id}`, {
        doctor_id: user.id,
        ...diagnosisForm
      });
      toast.success('Diagnosis template updated successfully');
      setDiagnosisForm({ diagnosis_name: '', description: '', associated_notes: [], level_2_diagnosis: [], notesTemplateSearch: '' });
      setShowEditDiagnosis(false);
      setEditingDiagnosis(null);
      loadDiagnosisTemplates();
    } catch (error) {
      console.error('Error updating diagnosis:', error);
      toast.error('Failed to update diagnosis template');
    }
  };

  const handleDeleteDiagnosis = async (id) => {
    if (!window.confirm('Are you sure you want to delete this diagnosis template?')) return;

    try {
      await axios.delete(`${API}/api/diagnosis-templates/${id}`);
      toast.success('Diagnosis template deleted successfully');
      loadDiagnosisTemplates();
    } catch (error) {
      console.error('Error deleting diagnosis:', error);
      toast.error('Failed to delete diagnosis template');
    }
  };

  // Doctor schedule management
  const [scheduleForm, setScheduleForm] = useState({ 
    schedule: '',
    leave_dates: []
  });
  
  useEffect(() => {
    const loadDoctorSchedule = async () => {
      try {
        const response = await axios.get(`${API}/api/doctors/${user.id}`);
        setScheduleForm({ 
          schedule: response.data.schedule || '09:00 AM - 05:00 PM',
          leave_dates: response.data.leave_dates || []
        });
      } catch (error) {
        console.error('Error loading doctor schedule:', error);
      }
    };
    
    if (user?.id) {
      loadDoctorSchedule();
    }
  }, [user]);

  const handleUpdateSchedule = async (e) => {
    e.preventDefault();
    try {
      // Get current doctor data
      const response = await axios.get(`${API}/api/doctors/${user.id}`);
      const currentDoctor = response.data;
      
      // Update with new schedule and leave dates
      // CRITICAL FIX: Include password to prevent deletion
      await axios.put(`${API}/api/doctors/${user.id}`, {
        name: currentDoctor.name,
        specialization: currentDoctor.specialization,
        contact: currentDoctor.contact,
        password: currentDoctor.password,
        photo_url: currentDoctor.photo_url,
        hospital_id: currentDoctor.hospital_id,
        hospital_ids: currentDoctor.hospital_ids,
        schedule: scheduleForm.schedule,
        leave_dates: scheduleForm.leave_dates
      });
      toast.success('Your working hours and leave dates updated successfully!');
    } catch (error) {
      console.error('Error updating schedule:', error);
      toast.error('Failed to update working hours');
    }
  };

  const handleLeaveDatesUpdate = (newLeaveDates) => {
    setScheduleForm(prev => ({ ...prev, leave_dates: newLeaveDates }));
  };

  const openEditDiagnosis = (diagnosis) => {
    setEditingDiagnosis(diagnosis);
    setDiagnosisForm({
      diagnosis_name: diagnosis.diagnosis_name,
      description: diagnosis.description || '',
      associated_notes: diagnosis.associated_notes || [],
      level_2_diagnosis: diagnosis.level_2_diagnosis || [],
      notesTemplateSearch: ''
    });
    setShowEditDiagnosis(true);
  };

  // Chief Complaint Template Functions
  // Additional Notes Template Functions
  const handleAddAdditionalNotes = async (e) => {
    e.preventDefault();
    
    try {
      await axios.post(`${API}/api/additional-notes-templates`, {
        doctor_id: user.id,
        ...additionalNotesForm
      });
      toast.success('Additional notes template added successfully');
      setAdditionalNotesForm({ note_title: '', note_content: '' });
      setShowAddAdditionalNotes(false);
      loadAdditionalNotesTemplates();
    } catch (error) {
      console.error('Error adding additional notes:', error);
      toast.error('Failed to add additional notes template');
    }
  };

  const handleEditAdditionalNotes = async (e) => {
    e.preventDefault();
    
    try {
      await axios.put(`${API}/api/additional-notes-templates/${editingAdditionalNotes.id}`, {
        doctor_id: user.id,
        ...additionalNotesForm
      });
      toast.success('Additional notes template updated successfully');
      setAdditionalNotesForm({ note_title: '', note_content: '' });
      setShowEditAdditionalNotes(false);
      setEditingAdditionalNotes(null);
      loadAdditionalNotesTemplates();
    } catch (error) {
      console.error('Error updating additional notes:', error);
      toast.error('Failed to update additional notes template');
    }
  };

  const handleDeleteAdditionalNotes = async (id) => {
    if (!window.confirm('Are you sure you want to delete this additional notes template?')) return;

    try {
      await axios.delete(`${API}/api/additional-notes-templates/${id}`);
      toast.success('Additional notes template deleted successfully');
      loadAdditionalNotesTemplates();
    } catch (error) {
      console.error('Error deleting additional notes:', error);
      toast.error('Failed to delete additional notes template');
    }
  };

  const openEditAdditionalNotes = (notes) => {
    setEditingAdditionalNotes(notes);
    setAdditionalNotesForm({
      note_title: notes.note_title,
      note_content: notes.note_content || ''
    });
    setShowEditAdditionalNotes(true);
  };

  // Bulk Import
  const handleBulkImport = async () => {
    if (!bulkImportText.trim()) {
      toast.error('Please paste medication data');
      return;
    }

    try {
      // Parse CSV-like format: medication_name,timing,duration,time_of_day
      const lines = bulkImportText.trim().split('\n');
      const medications = [];

      for (let line of lines) {
        if (!line.trim()) continue;
        
        const parts = line.split(',').map(p => p.trim());
        if (parts.length >= 3) {
          medications.push({
            doctor_id: user.id,
            medication_name: parts[0],
            timing: parts[1] || 'Before Food',
            duration: parts[2] || '7 days',
            time_of_day: parts[3] ? parts[3].split('|').map(t => t.trim()) : [],
            notes: parts[4] || ''
          });
        }
      }

      if (medications.length === 0) {
        toast.error('No valid medication data found');
        return;
      }

      await axios.post(`${API}/api/medication-templates/bulk-import/${user.id}`, medications);
      toast.success(`${medications.length} medications imported successfully`);
      setShowBulkImport(false);
      setBulkImportText('');
      loadMedicationTemplates();
    } catch (error) {
      console.error('Error bulk importing:', error);
      toast.error('Failed to import medications');
    }
  };

  const handleCSVUpload = async () => {
    if (!csvFile) {
      toast.error('Please select a CSV file');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('file', csvFile);

      const response = await axios.post(`${API}/api/medication-templates/bulk-import/${user.id}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const { inserted_count, skipped_count, error_count, message } = response.data;
      
      // Show detailed success message
      if (inserted_count > 0) {
        toast.success(message || `Imported ${inserted_count} medication templates successfully`);
      } else if (skipped_count > 0 && inserted_count === 0) {
        toast.info(`All ${skipped_count} medications already exist (skipped duplicates)`);
      } else if (error_count > 0) {
        toast.warning(message || 'Import completed with errors');
      } else {
        toast.info('No medications were imported');
      }

      setShowBulkImport(false);
      setCsvFile(null);
      setUploadMethod('paste');
      await loadMedicationTemplates();
    } catch (error) {
      console.error('Error importing CSV:', error);
      toast.error(error.response?.data?.detail || 'Failed to import medications from CSV');
    }
  };

  // CSV Upload Handler for Common Medications
  const handleCommonMedCSVUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const text = e.target.result;
        const lines = text.split('\n');
        const medications = [];

        // Skip header row
        for (let i = 1; i < lines.length; i++) {
          const line = lines[i].trim();
          if (!line) continue;

          const parts = line.split(',');
          if (parts.length >= 6) {
            medications.push({
              medication_name: parts[0]?.trim(),
              generic_name: parts[1]?.trim() || null,
              category: parts[2]?.trim() || null,
              default_dosage: parts[3]?.trim() || null,
              default_timing: parts[4]?.trim() || null,
              default_duration: parts[5]?.trim() || null,
              notes: parts[6]?.trim() || null
            });
          }
        }

        if (medications.length === 0) {
          toast.error('No valid data found in CSV');
          return;
        }

        const response = await axios.post(`${API}/api/common-medications/bulk-import`, medications);
        toast.success(response.data.message);
        setShowCommonMedUpload(false);
        loadCommonMedications();
      } catch (error) {
        console.error('Error uploading CSV:', error);
        toast.error('Failed to import CSV');
      }
    };
    reader.readAsText(file);
  };

  // CSV Upload Handler for Doctor Template
  const handleTemplateCSVUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const text = e.target.result;
        const lines = text.split('\n');
        const medications = [];

        // Skip header row
        for (let i = 1; i < lines.length; i++) {
          const line = lines[i].trim();
          if (!line) continue;

          const parts = line.split(',');
          if (parts.length >= 4) {
            medications.push({
              doctor_id: user.id,
              medication_name: parts[0]?.trim(),
              timing: parts[1]?.trim() || 'Before Food',
              duration: parts[2]?.trim() || '7 days',
              time_of_day: parts[3] ? parts[3].trim().split('|').map(t => t.trim()) : [],
              notes: parts[4]?.trim() || ''
            });
          }
        }

        if (medications.length === 0) {
          toast.error('No valid data found in CSV');
          return;
        }

        await axios.post(`${API}/api/medication-templates/bulk-import/${user.id}`, medications);
        toast.success(`${medications.length} templates imported successfully`);
        setShowTemplateCSVUpload(false);
        loadMedicationTemplates();
      } catch (error) {
        console.error('Error uploading CSV:', error);
        toast.error('Failed to import CSV');
      }
    };
    reader.readAsText(file);
  };

  // Toggle Settings
  const handleToggleCommonMedications = async (value) => {
    try {
      await axios.put(`${API}/api/doctor-settings/${user.id}?show_common_medications=${value}`);
      setDoctorSettings(prev => ({ ...prev, show_common_medications: value }));
      toast.success('Settings updated');
    } catch (error) {
      console.error('Error updating settings:', error);
      toast.error('Failed to update settings');
    }
  };

  // Toggle time of day
  const toggleTimeOfDay = (time) => {
    setMedicationForm(prev => ({
      ...prev,
      time_of_day: prev.time_of_day.includes(time)
        ? prev.time_of_day.filter(t => t !== time)
        : [...prev.time_of_day, time]
    }));
  };

  // Filter and sort templates alphabetically (using debounced search for better performance)
  const filteredMedications = medicationTemplates
    .filter(med =>
      med.medication_name.toLowerCase().includes(debouncedMedicationSearch.toLowerCase())
    )
    .sort((a, b) => a.medication_name.localeCompare(b.medication_name));

  const filteredChiefComplaints = chiefComplaintTemplates.filter(complaint =>
    complaint.complaint_name.toLowerCase().includes(chiefComplaintSearch.toLowerCase())
  );

  const filteredTests = testTemplates.filter(test =>
    test.test_name.toLowerCase().includes(testSearch.toLowerCase())
  );

  const filteredDiagnosis = diagnosisTemplates.filter(diagnosis =>
    diagnosis.diagnosis_name.toLowerCase().includes(debouncedDiagnosisSearch.toLowerCase())
  );

  const filteredAdditionalNotes = additionalNotesTemplates.filter(notes =>
    notes.note_title.toLowerCase().includes(additionalNotesSearch.toLowerCase())
  );

  // Helper to get additional notes template title by ID
  const getNotesTitle = (notesId) => {
    const notes = additionalNotesTemplates.find(n => n.id === notesId);
    return notes ? notes.note_title : 'Unknown';
  };

  return (
    <div className="space-y-6">
      {/* Doctor Working Hours Section */}
      <div className="bg-white rounded-xl shadow-sm border p-6">
        <div className="flex items-center space-x-3 mb-4">
          <Activity className="w-6 h-6 text-green-600" />
          <div>
            <h2 className="text-xl font-bold text-gray-900">Working Hours & Leave Management</h2>
            <p className="text-sm text-gray-500">Manage your availability schedule and leave dates</p>
          </div>
        </div>
        <form onSubmit={handleUpdateSchedule}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Working Hours */}
            <div className="space-y-3">
              <h3 className="font-semibold text-gray-900">Working Hours</h3>
              <div className="space-y-2">
                <Label htmlFor="schedule">Schedule *</Label>
                <Input
                  id="schedule"
                  value={scheduleForm.schedule}
                  onChange={(e) => setScheduleForm(prev => ({ ...prev, schedule: e.target.value }))}
                  placeholder="e.g., 09:00 AM - 05:00 PM"
                  required
                />
                <p className="text-xs text-gray-500">Format: HH:MM AM/PM - HH:MM AM/PM</p>
              </div>
            </div>

            {/* Leave Management */}
            <div className="space-y-3">
              <h3 className="font-semibold text-gray-900">Leave Dates</h3>
              <DoctorLeaveManagement
                leaveDates={scheduleForm.leave_dates}
                onUpdate={handleLeaveDatesUpdate}
              />
            </div>
          </div>
          
          <Button type="submit" className="mt-4 bg-green-600 hover:bg-green-700">
            Update Working Hours & Leave
          </Button>
        </form>
      </div>

      {/* Medication Templates Section */}
      <div className="bg-white rounded-xl shadow-sm border p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <Pill className="w-6 h-6 text-blue-600" />
            <div>
              <h2 className="text-xl font-bold text-gray-900">Medication Templates</h2>
              <p className="text-sm text-gray-500">Manage your medication library</p>
            </div>
          </div>
          <div className="flex space-x-2">
            <Button onClick={() => setShowBulkImport(true)} variant="outline" size="sm">
              <Upload className="w-4 h-4 mr-2" />
              Bulk Import
            </Button>
            <Button onClick={() => setShowAddMedication(true)} size="sm" className="bg-blue-600 hover:bg-blue-700">
              <Plus className="w-4 h-4 mr-2" />
              Add Medication
            </Button>
          </div>
        </div>

        {/* Search */}
        <div className="mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search medications..."
              value={medicationSearch}
              onChange={(e) => setMedicationSearch(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        {/* Medication List */}
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {filteredMedications.length > 0 ? (
            filteredMedications.map(med => (
              <div key={med.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50">
                <div className="flex items-start space-x-3 flex-1">
                  <div className="flex-shrink-0 mt-1">
                    <MedicationIcon 
                      type={med.medication_type} 
                      size={20} 
                      className="text-blue-600" 
                    />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900">{med.medication_name}</h3>
                    {(med.generic_name || med.treatment_for) && (
                      <div className="flex items-center space-x-4 mt-1 text-sm text-gray-500">
                        {med.generic_name && (
                          <span className="italic">Generic: {med.generic_name}</span>
                        )}
                        {med.generic_name && med.treatment_for && <span>•</span>}
                        {med.treatment_for && (
                          <span>For: {med.treatment_for}</span>
                        )}
                      </div>
                    )}
                    <div className="flex items-center space-x-4 mt-1 text-sm text-gray-600">
                      <span>{med.timing}</span>
                      <span>•</span>
                      <span>{med.duration}</span>
                      {med.time_of_day && med.time_of_day.length > 0 && (
                        <>
                          <span>•</span>
                          <span>{med.time_of_day.join(', ')}</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex space-x-2">
                  <Button variant="ghost" size="sm" onClick={() => openEditMedication(med)}>
                    <Edit2 className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => handleDeleteMedication(med.id)} className="text-red-600 hover:text-red-700">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-8 text-gray-500">
              <Pill className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p>No medication templates yet</p>
            </div>
          )}
        </div>
      </div>

      {/* Chief Complaint Templates Section */}
      <div className="bg-white rounded-xl shadow-sm border p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <Activity className="w-6 h-6 text-red-600" />
            <div>
              <h2 className="text-xl font-bold text-gray-900">Chief Complaint Templates</h2>
              <p className="text-sm text-gray-500">Quick selection for common chief complaints with associated medications and tests</p>
            </div>
          </div>
          <Button onClick={() => setShowAddChiefComplaint(true)} size="sm" className="bg-red-600 hover:bg-red-700">
            <Plus className="w-4 h-4 mr-1" />
            Add Chief Complaint
          </Button>
        </div>

        {/* Search */}
        <div className="mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search chief complaint templates..."
              value={chiefComplaintSearch}
              onChange={(e) => setChiefComplaintSearch(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        {/* Chief Complaint Templates List */}
        <div className="space-y-3 max-h-96 overflow-y-auto">
          {filteredChiefComplaints.length > 0 ? (
            filteredChiefComplaints.map(complaint => (
              <div key={complaint.id} className="flex items-start justify-between p-4 border rounded-lg hover:bg-gray-50">
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900">{complaint.complaint_name}</h3>
                  {complaint.description && (
                    <p className="text-sm text-gray-600 mt-1">{complaint.description}</p>
                  )}
                  {complaint.sub_complaints && complaint.sub_complaints.length > 0 && (
                    <div className="mt-2">
                      <span className="text-xs font-medium text-blue-600 mr-1">Level 2:</span>
                      <span className="text-xs text-gray-700">{complaint.sub_complaints.length} options</span>
                      <div className="mt-1 flex flex-wrap gap-1">
                        {complaint.sub_complaints.slice(0, 3).map((sub, idx) => {
                          const subName = typeof sub === 'string' ? sub : sub.name;
                          const subMedCount = typeof sub === 'object' ? (sub.associated_medications || []).length : 0;
                          const displayText = subName.length > 30 ? subName.substring(0, 30) + '...' : subName;
                          
                          return (
                            <span key={idx} className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded flex items-center gap-1">
                              {displayText}
                              {subMedCount > 0 && <span className="text-blue-500">({subMedCount}💊)</span>}
                            </span>
                          );
                        })}
                        {complaint.sub_complaints.length > 3 && (
                          <span className="text-xs text-gray-500">+{complaint.sub_complaints.length - 3} more</span>
                        )}
                      </div>
                    </div>
                  )}
                  {complaint.associated_medications && complaint.associated_medications.length > 0 && (
                    <div className="mt-2">
                      <span className="text-xs font-medium text-gray-600 mr-1">Medications:</span>
                      <span className="text-xs text-gray-700">{complaint.associated_medications.length} associated</span>
                    </div>
                  )}
                  {complaint.associated_tests && complaint.associated_tests.length > 0 && (
                    <div className="mt-1">
                      <span className="text-xs font-medium text-gray-600 mr-1">Tests:</span>
                      <span className="text-xs text-gray-700">{complaint.associated_tests.length} associated</span>
                    </div>
                  )}
                </div>
                <div className="flex items-center space-x-2 ml-4">
                  <Button variant="ghost" size="sm" onClick={() => openEditChiefComplaint(complaint)} className="text-blue-600 hover:text-blue-700">
                    <Edit2 className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => handleDeleteChiefComplaint(complaint.id)} className="text-red-600 hover:text-red-700">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-8 text-gray-500">
              <Activity className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p>No chief complaint templates yet</p>
            </div>
          )}
        </div>
      </div>

      {/* Test Templates Section */}
      <div className="bg-white rounded-xl shadow-sm border p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <Activity className="w-6 h-6 text-indigo-600" />
            <div>
              <h2 className="text-xl font-bold text-gray-900">Test Templates</h2>
              <p className="text-sm text-gray-500">Quick selection for common diagnostic tests</p>
            </div>
          </div>
          <Button onClick={() => setShowAddTest(true)} size="sm" className="bg-indigo-600 hover:bg-indigo-700">
            <Plus className="w-4 h-4 mr-1" />
            Add Test
          </Button>
        </div>

        {/* Search */}
        <div className="mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search test templates..."
              value={testSearch}
              onChange={(e) => setTestSearch(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        {/* Test Templates List - Grouped by Test Type and Category */}
        <div className="space-y-3">
          {(() => {
            if (filteredTests.length === 0) {
              return (
                <div className="text-center py-8 text-gray-500">
                  <Activity className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p>No test templates yet</p>
                </div>
              );
            }

            // Group ALL tests by test type
            const groupTestsByType = (tests) => {
              return tests.reduce((acc, test) => {
                const desc = test.description || '';
                const testType = desc.split(' - ')[0] || 'My Custom Tests';
                if (!acc[testType]) acc[testType] = [];
                acc[testType].push(test);
                return acc;
              }, {});
            };

            const groupedTests = groupTestsByType(filteredTests);
            
            // Auto-expand when searching
            const isExpanded = (key) => testSearch ? true : expandedTestTypes[key];
            const isCategoryExpanded = (key) => testSearch ? true : expandedTestCategories[key];

            return Object.entries(groupedTests).map(([testType, tests]) => {
              // Check if this is a Blood Test type (has categories)
              const isBloodTest = testType === 'Blood Test';
              
              // For Blood Tests, group by category
              const groupedByCategory = isBloodTest 
                ? tests.reduce((acc, test) => {
                    const category = (test.description || '').split(' - ')[1] || 'Uncategorized';
                    if (!acc[category]) acc[category] = [];
                    acc[category].push(test);
                    return acc;
                  }, {})
                : null;

              return (
                <div key={testType} className="border border-gray-200 rounded-lg">
                  {/* Test Type Header */}
                  <button
                    onClick={() => setExpandedTestTypes(prev => ({ ...prev, [testType]: !prev[testType] }))}
                    className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 transition-colors rounded-t"
                  >
                    <div className="flex items-center space-x-2">
                      {isExpanded(testType) ? (
                        <ChevronDown className="w-4 h-4 text-gray-600" />
                      ) : (
                        <ChevronRight className="w-4 h-4 text-gray-600" />
                      )}
                      <span className="font-semibold text-gray-900">{testType}</span>
                      <span className="text-xs text-gray-600 bg-white px-2 py-0.5 rounded">{tests.length} tests</span>
                    </div>
                  </button>

                  {/* Test Type Content */}
                  {isExpanded(testType) && (
                    <div className="p-2">
                      {groupedByCategory ? (
                        // Blood Tests grouped by category
                        Object.entries(groupedByCategory).map(([category, categoryTests]) => (
                          <div key={category} className="mb-2">
                            {/* Category Header */}
                            <button
                              onClick={() => setExpandedTestCategories(prev => ({ ...prev, [category]: !prev[category] }))}
                              className="w-full flex items-center justify-between p-2 bg-blue-50 hover:bg-blue-100 transition-colors rounded"
                            >
                              <div className="flex items-center space-x-2">
                                {isCategoryExpanded(category) ? (
                                  <ChevronDown className="w-3 h-3 text-blue-600" />
                                ) : (
                                  <ChevronRight className="w-3 h-3 text-blue-600" />
                                )}
                                <span className="text-sm font-medium text-blue-900">{category}</span>
                                <span className="text-xs text-blue-600 bg-white px-2 py-0.5 rounded">{categoryTests.length}</span>
                              </div>
                            </button>

                            {/* Category Tests */}
                            {isCategoryExpanded(category) && (
                              <div className="mt-1 space-y-1 ml-4">
                                {categoryTests.map((test) => {
                                  const isCommonTest = test.doctor_id === 'common';
                                  return (
                                    <div 
                                      key={test.id} 
                                      className={`flex items-center justify-between p-3 border border-gray-200 rounded hover:shadow-sm transition-shadow ${isCommonTest ? 'bg-blue-50' : 'bg-white'}`}
                                    >
                                      <div className="flex-1">
                                        <div className="flex items-center space-x-2">
                                          <h5 className="font-medium text-gray-900">{test.test_name}</h5>
                                          {isCommonTest && (
                                            <span className="text-xs bg-blue-600 text-white px-2 py-0.5 rounded">Default</span>
                                          )}
                                        </div>
                                      </div>
                                      {!isCommonTest && (
                                        <div className="flex items-center space-x-2 ml-4">
                                          <Button variant="ghost" size="sm" onClick={() => openEditTest(test)} className="text-blue-600 hover:text-blue-700">
                                            <Edit2 className="w-4 h-4" />
                                          </Button>
                                          <Button variant="ghost" size="sm" onClick={() => handleDeleteTest(test.id)} className="text-red-600 hover:text-red-700">
                                            <Trash2 className="w-4 h-4" />
                                          </Button>
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        ))
                      ) : (
                        // Non-Blood Tests (no categories)
                        <div className="space-y-1">
                          {tests.map(test => {
                            const isCommonTest = test.doctor_id === 'common';
                            return (
                              <div 
                                key={test.id} 
                                className={`flex items-center justify-between p-3 border border-gray-200 rounded hover:shadow-sm transition-shadow ${isCommonTest ? 'bg-blue-50' : 'bg-white'}`}
                              >
                                <div className="flex-1">
                                  <div className="flex items-center space-x-2">
                                    <h5 className="font-medium text-gray-900">{test.test_name}</h5>
                                    {isCommonTest && (
                                      <span className="text-xs bg-blue-600 text-white px-2 py-0.5 rounded">Default</span>
                                    )}
                                  </div>
                                </div>
                                {!isCommonTest && (
                                  <div className="flex items-center space-x-2 ml-4">
                                    <Button variant="ghost" size="sm" onClick={() => openEditTest(test)} className="text-blue-600 hover:text-blue-700">
                                      <Edit2 className="w-4 h-4" />
                                    </Button>
                                    <Button variant="ghost" size="sm" onClick={() => handleDeleteTest(test.id)} className="text-red-600 hover:text-red-700">
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            });
          })()}
        </div>
      </div>

      {/* Advice Templates Section */}
      <div className="bg-white rounded-xl shadow-sm border p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <FileText className="w-6 h-6 text-teal-600" />
            <div>
              <h2 className="text-xl font-bold text-gray-900">Advice</h2>
              <p className="text-sm text-gray-500">Quick notes for diagnosis templates</p>
            </div>
          </div>
          <Button onClick={() => setShowAddAdditionalNotes(true)} size="sm" className="bg-teal-600 hover:bg-teal-700">
            <Plus className="w-4 h-4 mr-1" />
            Add Notes
          </Button>
        </div>

        {/* Search */}
        <div className="mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search additional notes templates..."
              value={additionalNotesSearch}
              onChange={(e) => setAdditionalNotesSearch(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        {/* Advice List */}
        <div className="space-y-3 max-h-96 overflow-y-auto">
          {filteredAdditionalNotes.length > 0 ? (
            filteredAdditionalNotes.map(notes => (
              <div key={notes.id} className="flex items-start justify-between p-4 border rounded-lg hover:bg-gray-50">
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900">{notes.note_title}</h3>
                  {notes.note_content && (
                    <p className="text-sm text-gray-600 mt-1">{notes.note_content}</p>
                  )}
                </div>
                <div className="flex items-center space-x-2 ml-4">
                  <Button variant="ghost" size="sm" onClick={() => openEditAdditionalNotes(notes)} className="text-blue-600 hover:text-blue-700">
                    <Edit2 className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => handleDeleteAdditionalNotes(notes.id)} className="text-red-600 hover:text-red-700">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-8 text-gray-500">
              <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p>No additional notes templates yet</p>
            </div>
          )}
        </div>
      </div>

      {/* Diagnosis Templates Section */}
      <div className="bg-white rounded-xl shadow-sm border p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <Activity className="w-6 h-6 text-orange-600" />
            <div>
              <h2 className="text-xl font-bold text-gray-900">Diagnosis Templates</h2>
              <p className="text-sm text-gray-500">Quick selection for common diagnoses</p>
            </div>
          </div>
          <Button onClick={() => setShowAddDiagnosis(true)} size="sm" className="bg-orange-600 hover:bg-orange-700">
            <Plus className="w-4 h-4 mr-1" />
            Add Diagnosis
          </Button>
        </div>

        {/* Search */}
        <div className="mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search diagnosis templates..."
              value={diagnosisSearch}
              onChange={(e) => setDiagnosisSearch(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        {/* Diagnosis Templates List */}
        <div className="space-y-3 max-h-96 overflow-y-auto">
          {filteredDiagnosis.length > 0 ? (
            filteredDiagnosis.map(diagnosis => (
              <div key={diagnosis.id} className="flex items-start justify-between p-4 border rounded-lg hover:bg-gray-50">
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900">{diagnosis.diagnosis_name}</h3>
                  {diagnosis.description && (
                    <p className="text-sm text-gray-600 mt-1">{diagnosis.description}</p>
                  )}
                  
                  {/* Level 2 and Level 3 Diagnosis Preview */}
                  {diagnosis.level_2_diagnosis && diagnosis.level_2_diagnosis.length > 0 && (
                    <div className="mt-2 pl-3 border-l-2 border-orange-300">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-medium text-orange-600">Level 2:</span>
                        <span className="text-xs text-gray-700">{diagnosis.level_2_diagnosis.length} option{diagnosis.level_2_diagnosis.length !== 1 ? 's' : ''}</span>
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {diagnosis.level_2_diagnosis.slice(0, 3).map((level2, idx) => {
                          const level2Name = typeof level2 === 'string' ? level2 : level2.name;
                          const level3Count = typeof level2 === 'object' && level2.level_3_diagnosis ? level2.level_3_diagnosis.length : 0;
                          return (
                            <span key={idx} className="text-xs px-2 py-1 bg-orange-50 text-orange-700 rounded">
                              {level2Name}{level3Count > 0 && <span className="ml-1 text-orange-500">({level3Count})</span>}
                            </span>
                          );
                        })}
                        {diagnosis.level_2_diagnosis.length > 3 && (
                          <span className="text-xs text-gray-500">+{diagnosis.level_2_diagnosis.length - 3} more</span>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {diagnosis.associated_notes && diagnosis.associated_notes.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      <span className="text-xs font-medium text-gray-600 mr-1">Advice:</span>
                      {diagnosis.associated_notes.map((notesId, idx) => (
                        <span 
                          key={idx}
                          className="px-2 py-1 rounded text-xs font-medium bg-teal-100 text-teal-700"
                        >
                          {getNotesTitle(notesId)}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex items-center space-x-2 ml-4">
                  <Button variant="ghost" size="sm" onClick={() => openEditDiagnosis(diagnosis)} className="text-blue-600 hover:text-blue-700">
                    <Edit2 className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => handleDeleteDiagnosis(diagnosis.id)} className="text-red-600 hover:text-red-700">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-8 text-gray-500">
              <Activity className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p>No diagnosis templates yet</p>
            </div>
          )}
        </div>
      </div>

      {/* Settings Section */}
      <div className="bg-white rounded-xl shadow-sm border p-6">
        <div className="flex items-center space-x-3 mb-6">
          <Activity className="w-6 h-6 text-green-600" />
          <div>
            <h2 className="text-xl font-bold text-gray-900">Settings</h2>
            <p className="text-sm text-gray-500">Configure your template preferences</p>
          </div>
        </div>

        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 border rounded-lg">
            <div>
              <h3 className="font-semibold text-gray-900">Show Common Medications</h3>
              <p className="text-sm text-gray-600 mt-1">
                Display global pediatric medications in dropdown when adding medical sessions
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={doctorSettings.show_common_medications}
                onChange={(e) => handleToggleCommonMedications(e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>
        </div>
      </div>

      {/* Common Medications Section */}
      <div className="bg-white rounded-xl shadow-sm border p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <Pill className="w-6 h-6 text-green-600" />
            <div>
              <h2 className="text-xl font-bold text-gray-900">Common Medications</h2>
              <p className="text-sm text-gray-500">Global medication database (managed by Central Admin)</p>
            </div>
          </div>
        </div>

        {/* Info Banner */}
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-800">
            <strong>Note:</strong> This is a read-only view. Only Central Admins can add, edit, or delete common medications.
          </p>
        </div>

        {/* Search */}
        <div className="mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search common medications..."
              value={commonMedSearch}
              onChange={(e) => setCommonMedSearch(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        {/* Common Medications List */}
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {commonMedications
            .filter(med => 
              med.medication_name.toLowerCase().includes(commonMedSearch.toLowerCase()) ||
              (med.generic_name && med.generic_name.toLowerCase().includes(commonMedSearch.toLowerCase())) ||
              (med.category && med.category.toLowerCase().includes(commonMedSearch.toLowerCase()))
            )
            .length > 0 ? (
            commonMedications
              .filter(med => 
                med.medication_name.toLowerCase().includes(commonMedSearch.toLowerCase()) ||
                (med.generic_name && med.generic_name.toLowerCase().includes(commonMedSearch.toLowerCase())) ||
                (med.category && med.category.toLowerCase().includes(commonMedSearch.toLowerCase()))
              )
              .map(med => (
                <div key={med.id} className="flex items-start p-4 border rounded-lg bg-gray-50">
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900">{med.medication_name}</h3>
                    {med.generic_name && (
                      <p className="text-xs text-gray-500 mt-1">Generic: {med.generic_name}</p>
                    )}
                    <div className="flex items-center flex-wrap gap-3 mt-2 text-sm text-gray-600">
                      {med.category && (
                        <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs font-medium">
                          {med.category}
                        </span>
                      )}
                      {med.default_dosage && <span>Dosage: {med.default_dosage}</span>}
                      {med.default_timing && (
                        <>
                          <span>•</span>
                          <span>{med.default_timing}</span>
                        </>
                      )}
                      {med.default_duration && (
                        <>
                          <span>•</span>
                          <span>{med.default_duration}</span>
                        </>
                      )}
                    </div>
                    {med.notes && (
                      <p className="text-xs text-gray-500 mt-2 italic">{med.notes}</p>
                    )}
                  </div>
                </div>
              ))
          ) : (
            <div className="text-center py-8 text-gray-500">
              <Pill className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p>No common medications found</p>
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit Medication Dialog - Conditionally rendered for performance */}
      {(showAddMedication || showEditMedication) && (
      <Dialog open={true} onOpenChange={(open) => {
        if (!open) {
          setShowAddMedication(false);
          setShowEditMedication(false);
          setEditingMedication(null);
        }
      }}>
        <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingMedication ? 'Edit' : 'Add'} Medication Template</DialogTitle>
          </DialogHeader>
          <form onSubmit={editingMedication ? handleEditMedication : handleAddMedication} className="space-y-4">
            <div>
              <Label>Medication Type (Optional)</Label>
              <div className="grid grid-cols-4 gap-2 mt-2">
                {['injection', 'pill', 'syrup', 'tablet'].map((type) => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => setMedicationForm(prev => ({ ...prev, medication_type: type }))}
                    className={`flex flex-col items-center p-3 rounded-lg border-2 transition-all hover:bg-gray-50 ${
                      medicationForm.medication_type === type
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200'
                    }`}
                  >
                    <MedicationIcon type={type} size={24} className={`mb-1 ${
                      medicationForm.medication_type === type ? 'text-blue-600' : 'text-gray-400'
                    }`} />
                    <span className={`text-xs capitalize ${
                      medicationForm.medication_type === type ? 'text-blue-600 font-medium' : 'text-gray-500'
                    }`}>
                      {type}
                    </span>
                  </button>
                ))}
              </div>
            </div>
            
            <div>
              <Label>Medication Name *</Label>
              <Input
                placeholder="e.g., Paracetamol 500mg"
                value={medicationForm.medication_name}
                onChange={(e) => setMedicationForm(prev => ({ ...prev, medication_name: e.target.value }))}
                required
              />
            </div>

            <div>
              <Label>Generic Name *</Label>
              <Input
                placeholder="e.g., Acetaminophen"
                value={medicationForm.generic_name}
                onChange={(e) => setMedicationForm(prev => ({ ...prev, generic_name: e.target.value }))}
                required
              />
            </div>

            <div>
              <Label>Manufacturer</Label>
              <Input
                placeholder="e.g., Cipla, Sun Pharma, Dr. Reddy's"
                value={medicationForm.manufacturer}
                onChange={(e) => setMedicationForm(prev => ({ ...prev, manufacturer: e.target.value }))}
              />
            </div>

            <div>
              <Label>Category</Label>
              <Input
                placeholder="e.g., Analgesic, Antibiotic, Antihistamine"
                value={medicationForm.category}
                onChange={(e) => setMedicationForm(prev => ({ ...prev, category: e.target.value }))}
              />
            </div>

            <div>
              <Label>Treatment For</Label>
              <Input
                placeholder="e.g., Fever, Pain, Infection"
                value={medicationForm.treatment_for}
                onChange={(e) => setMedicationForm(prev => ({ ...prev, treatment_for: e.target.value }))}
              />
            </div>

            <div>
              <Label>Time of Day & Frequency</Label>
              
              {/* Time of Day */}
              <div className="flex items-center gap-3 mt-2 ml-1">
                <span className="text-xs text-gray-600 w-20">Time:</span>
                {['Morning', 'Afternoon', 'Evening', 'Night'].map(time => (
                  <button
                    key={time}
                    type="button"
                    onClick={() => toggleTimeOfDay(time)}
                    className="flex flex-col items-center gap-1 group"
                  >
                    <div className={`w-2.5 h-2.5 rounded-full transition-all ${
                      medicationForm.time_of_day.includes(time)
                        ? 'bg-purple-600 scale-125'
                        : 'bg-gray-300 group-hover:bg-purple-400'
                    }`} />
                    <span className={`text-[10px] transition-colors ${
                      medicationForm.time_of_day.includes(time)
                        ? 'text-purple-600 font-medium'
                        : 'text-gray-500 group-hover:text-purple-500'
                    }`}>
                      {time.substring(0, 1)}
                    </span>
                  </button>
                ))}
              </div>
              
              {/* Frequency Buttons */}
              <div className="flex items-center gap-2 mt-2 ml-1">
                <span className="text-xs text-gray-600 w-20">Frequency:</span>
                {['Once a day', 'Once a week', 'Once a month'].map((freq) => {
                  const isSelected = medicationForm.frequency === freq;
                  return (
                    <button
                      key={freq}
                      type="button"
                      onClick={() => {
                        setMedicationForm(prev => ({ ...prev, frequency: isSelected ? '' : freq }));
                      }}
                      className={`px-2 py-1 text-[10px] rounded transition-all ${
                        isSelected
                          ? 'bg-purple-600 text-white'
                          : 'bg-gray-200 text-gray-600 hover:bg-purple-100'
                      }`}
                    >
                      {freq}
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <Label>Timing *</Label>
              <Select value={medicationForm.timing} onValueChange={(value) => setMedicationForm(prev => ({ ...prev, timing: value }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Before Food">Before Food</SelectItem>
                  <SelectItem value="After Food">After Food</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Duration</Label>
              <Input
                placeholder="e.g., 7 days, 2 weeks"
                value={medicationForm.duration}
                onChange={(e) => setMedicationForm(prev => ({ ...prev, duration: e.target.value }))}
              />
            </div>

            <div>
              <Label>Notes (Optional)</Label>
              <Textarea
                placeholder="Additional instructions..."
                value={medicationForm.notes}
                onChange={(e) => setMedicationForm(prev => ({ ...prev, notes: e.target.value }))}
                rows={2}
              />
            </div>

            <div className="flex space-x-2 pt-4">
              <Button type="submit" className="flex-1">Save Template</Button>
              <Button type="button" variant="outline" onClick={() => {
                setShowAddMedication(false);
                setShowEditMedication(false);
                setEditingMedication(null);
              }} className="flex-1">Cancel</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
      )}


      {/* Add/Edit Test Dialog - Conditionally rendered for performance */}
      {(showAddTest || showEditTest) && (
        <Dialog open={true} onOpenChange={(open) => {
          if (!open) {
            setShowAddTest(false);
            setShowEditTest(false);
            setEditingTest(null);
            setTestForm({ test_name: '', test_type: 'Blood Test', category: '', description: '' });
          }
        }}>
          <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingTest ? 'Edit Test Template' : 'Add Test Template'}</DialogTitle>
              <DialogDescription>
                {editingTest ? 'Update your test template' : 'Create a new test template for quick selection'}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={editingTest ? handleEditTest : handleAddTest} className="space-y-4">
              <div>
                <Label htmlFor="test_name">Test Name *</Label>
                <Input
                  id="test_name"
                  placeholder="e.g., Complete Blood Count (CBC)"
                  value={testForm.test_name}
                  onChange={(e) => setTestForm(prev => ({ ...prev, test_name: e.target.value }))}
                  required
                  autoFocus
                />
              </div>
              
              <div>
                <Label htmlFor="test_type">Test Type *</Label>
                <select
                  id="test_type"
                  value={testForm.test_type}
                  onChange={(e) => setTestForm(prev => ({ ...prev, test_type: e.target.value, category: '' }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  required
                >
                  <option value="Blood Test">Blood Test</option>
                  <option value="Urine Test">Urine Test</option>
                  <option value="Imaging">Imaging</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              {testForm.test_type === 'Blood Test' && (
                <div>
                  <Label htmlFor="category">Category *</Label>
                  <select
                    id="category"
                    value={testForm.category}
                    onChange={(e) => setTestForm(prev => ({ ...prev, category: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    required
                  >
                    <option value="">Select Category</option>
                    <option value="Diabetic Screen">Diabetic Screen</option>
                    <option value="Heart Screen">Heart Screen</option>
                    <option value="Liver Screen">Liver Screen</option>
                    <option value="Kidney Screen">Kidney Screen</option>
                    <option value="Minerals">Minerals</option>
                    <option value="Hormones Screen">Hormones Screen</option>
                    <option value="Vitamins">Vitamins</option>
                    <option value="Anemia & Inflammatory Screen">Anemia & Inflammatory Screen</option>
                    <option value="General Body Screen">General Body Screen</option>
                  </select>
                </div>
              )}
              
              <div>
                <Label htmlFor="description">Description (Optional)</Label>
                <Textarea
                  id="description"
                  placeholder="Additional details about the test..."
                  value={testForm.description}
                  onChange={(e) => setTestForm(prev => ({ ...prev, description: e.target.value }))}
                  rows={3}
                />
              </div>
              
              <div className="flex space-x-2 pt-4">
                <Button type="submit" className="flex-1 bg-indigo-600 hover:bg-indigo-700">Save Template</Button>
                <Button type="button" variant="outline" onClick={() => {
                  setShowAddTest(false);
                  setShowEditTest(false);
                  setEditingTest(null);
                  setTestForm({ test_name: '', test_type: 'Blood Test', category: '', description: '' });
                }} className="flex-1">Cancel</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      )}

      {/* Add/Edit Diagnosis Dialog - Conditionally rendered for performance */}
      {(showAddDiagnosis || showEditDiagnosis) && (
        <Dialog open={true} onOpenChange={(open) => {
          if (!open) {
            setShowAddDiagnosis(false);
            setShowEditDiagnosis(false);
            setEditingDiagnosis(null);
          }
        }}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingDiagnosis ? 'Edit Diagnosis Template' : 'Add Diagnosis Template'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={editingDiagnosis ? handleEditDiagnosis : handleAddDiagnosis} className="space-y-4">
              <div>
                <Label>Diagnosis Name *</Label>
                <Input
                  placeholder="e.g., Upper Respiratory Tract Infection"
                  value={diagnosisForm.diagnosis_name}
                  onChange={(e) => setDiagnosisForm(prev => ({ ...prev, diagnosis_name: e.target.value }))}
                  required
                  autoFocus
                />
              </div>
              
              <div>
                <Label>Description (Optional)</Label>
                <Textarea
                  placeholder="Additional details about the diagnosis..."
                  value={diagnosisForm.description}
                  onChange={(e) => setDiagnosisForm(prev => ({ ...prev, description: e.target.value }))}
                  rows={3}
                />
              </div>

              {/* Level 2 Diagnosis Section */}
              <div>
              <Label className="flex items-center gap-2">
                Level 2 Diagnosis (Optional)
                <span className="text-xs text-gray-500 font-normal">Add more specific diagnoses</span>
              </Label>
              
              {diagnosisForm.level_2_diagnosis.length > 0 && (
                <div className="space-y-2 mb-2 mt-2">
                  {diagnosisForm.level_2_diagnosis.map((level2, index) => {
                    const level2Name = typeof level2 === 'string' ? level2 : level2.name;
                    const level3List = typeof level2 === 'object' ? level2.level_3_diagnosis || [] : [];
                    
                    return (
                      <div key={index} className="p-3 bg-orange-50 border border-orange-200 rounded">
                        <div className="flex items-start gap-2">
                          <span className="text-sm flex-1 font-medium text-gray-900">{level2Name}</span>
                          <button
                            type="button"
                            onClick={() => {
                              setManagingLevel3ForDiagnosisIndex(index);
                            }}
                            className="text-purple-600 hover:text-purple-800 text-xs whitespace-nowrap"
                          >
                            + Level 3
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setEditingLevel2DiagnosisIndex(index);
                              setShowLevel2DiagnosisNotes(true);
                            }}
                            className="text-teal-600 hover:text-teal-800 text-xs"
                          >
                            + Advice
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setDiagnosisForm(prev => ({
                                ...prev,
                                level_2_diagnosis: prev.level_2_diagnosis.filter((_, i) => i !== index)
                              }));
                            }}
                            className="text-red-600 hover:text-red-800"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                        <div className="mt-2 flex gap-3 text-xs text-gray-600">
                          {typeof level2 === 'object' && level2.associated_notes && level2.associated_notes.length > 0 && (
                            <span>
                              <span className="font-medium">Advice:</span> {level2.associated_notes.length}
                            </span>
                          )}
                          {level3List.length > 0 && (
                            <span>
                              <span className="font-medium">Level 3:</span> {level3List.length}
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              
              {/* Add new Level 2 diagnosis */}
              <div className="flex gap-2 mt-2">
                <Input
                  placeholder="e.g., Viral URTI, Bacterial URTI"
                  value={newLevel2Diagnosis}
                  onChange={(e) => setNewLevel2Diagnosis(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      if (newLevel2Diagnosis.trim()) {
                        setDiagnosisForm(prev => ({
                          ...prev,
                          level_2_diagnosis: [...prev.level_2_diagnosis, { name: newLevel2Diagnosis.trim(), associated_notes: [], level_3_diagnosis: [] }]
                        }));
                        setNewLevel2Diagnosis('');
                      }
                    }
                  }}
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    if (newLevel2Diagnosis.trim()) {
                      setDiagnosisForm(prev => ({
                        ...prev,
                        level_2_diagnosis: [...prev.level_2_diagnosis, { name: newLevel2Diagnosis.trim(), associated_notes: [], level_3_diagnosis: [] }]
                      }));
                      setNewLevel2Diagnosis('');
                    }
                  }}
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </div>

            <div>
              <Label>Advice (Optional)</Label>
              
              {/* Advice */}
              <div className="mt-2">
                <p className="text-xs font-medium text-gray-700 mb-2">Your Advice</p>
                
                {/* Search for Advice */}
                {additionalNotesTemplates.length > 0 && (
                  <div className="relative mb-2">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      placeholder="Search your additional notes templates..."
                      value={diagnosisForm.notesTemplateSearch || ''}
                      onChange={(e) => setDiagnosisForm(prev => ({ ...prev, notesTemplateSearch: e.target.value }))}
                      className="pl-10 text-sm"
                    />
                  </div>
                )}
                
                <div className="space-y-2 max-h-48 overflow-y-auto border rounded-lg p-3">
                  {additionalNotesTemplates.length > 0 ? (
                    additionalNotesTemplates
                      .filter(notes => 
                        notes.note_title.toLowerCase().includes((diagnosisForm.notesTemplateSearch || '').toLowerCase())
                      )
                      .map(notes => (
                        <label key={notes.id} className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 p-2 rounded">
                          <input
                            type="checkbox"
                            checked={diagnosisForm.associated_notes.includes(notes.id)}
                            onChange={() => {
                              setDiagnosisForm(prev => ({
                                ...prev,
                                associated_notes: prev.associated_notes.includes(notes.id)
                                  ? prev.associated_notes.filter(id => id !== notes.id)
                                  : [...prev.associated_notes, notes.id]
                              }));
                            }}
                            className="w-4 h-4 text-teal-600"
                          />
                          <span className="text-sm">{notes.note_title}</span>
                        </label>
                      ))
                  ) : (
                    <p className="text-sm text-gray-500">No additional notes templates available. Create one below first.</p>
                  )}
                  {additionalNotesTemplates.length > 0 && 
                   additionalNotesTemplates.filter(notes => 
                     notes.note_title.toLowerCase().includes((diagnosisForm.notesTemplateSearch || '').toLowerCase())
                   ).length === 0 && (
                    <p className="text-sm text-gray-500">No notes found matching your search.</p>
                  )}
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-1">This advice will be suggested when this diagnosis is selected</p>
            </div>
            
            <div className="flex space-x-2 pt-4">
              <Button type="submit" className="flex-1 bg-orange-600 hover:bg-orange-700">Save Template</Button>
              <Button type="button" variant="outline" onClick={() => {
                setShowAddDiagnosis(false);
                setShowEditDiagnosis(false);
                setEditingDiagnosis(null);
              }} className="flex-1">Cancel</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
      )}

      {/* Level 3 Diagnosis Management Dialog - Conditionally rendered */}
      {managingLevel3ForDiagnosisIndex !== null && (
        <Dialog open={true} onOpenChange={(open) => {
          if (!open) {
            setManagingLevel3ForDiagnosisIndex(null);
            setNewLevel3Diagnosis('');
          }
        }}>
        <DialogContent className="max-w-2xl max-h-[600px] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Manage Level 3 Diagnosis
              {managingLevel3ForDiagnosisIndex !== null && diagnosisForm.level_2_diagnosis[managingLevel3ForDiagnosisIndex] && (
                <span className="block text-sm font-normal text-gray-600 mt-1">
                  For: {typeof diagnosisForm.level_2_diagnosis[managingLevel3ForDiagnosisIndex] === 'string' 
                    ? diagnosisForm.level_2_diagnosis[managingLevel3ForDiagnosisIndex]
                    : diagnosisForm.level_2_diagnosis[managingLevel3ForDiagnosisIndex].name}
                </span>
              )}
            </DialogTitle>
          </DialogHeader>
          
          {managingLevel3ForDiagnosisIndex !== null && (() => {
            const currentLevel2 = diagnosisForm.level_2_diagnosis[managingLevel3ForDiagnosisIndex];
            const level3List = typeof currentLevel2 === 'object' ? currentLevel2.level_3_diagnosis || [] : [];
            
            return (
              <>
                {level3List.length > 0 && (
                  <div className="space-y-2 mb-4">
                    <Label>Existing Level 3 Diagnosis</Label>
                    {level3List.map((level3, index) => {
                      const level3Name = typeof level3 === 'string' ? level3 : level3.name;
                      const level3Notes = typeof level3 === 'object' ? level3.associated_notes || [] : [];
                      
                      return (
                        <div key={index} className="p-3 bg-purple-50 border border-purple-200 rounded">
                          <div className="flex items-start gap-2">
                            <span className="text-sm flex-1 font-medium text-gray-900">{level3Name}</span>
                            <button
                              type="button"
                              onClick={() => {
                                setEditingLevel3DiagnosisIndex(index);
                                setShowLevel3DiagnosisNotes(true);
                              }}
                              className="text-teal-600 hover:text-teal-800 text-xs"
                            >
                              + Advice
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setDiagnosisForm(prev => {
                                  const updated = [...prev.level_2_diagnosis];
                                  const currentL2 = updated[managingLevel3ForDiagnosisIndex];
                                  const l2Obj = typeof currentL2 === 'string' 
                                    ? { name: currentL2, associated_notes: [], level_3_diagnosis: [] }
                                    : { ...currentL2 };
                                  
                                  l2Obj.level_3_diagnosis = (l2Obj.level_3_diagnosis || []).filter((_, i) => i !== index);
                                  updated[managingLevel3ForDiagnosisIndex] = l2Obj;
                                  return { ...prev, level_2_diagnosis: updated };
                                });
                              }}
                              className="text-red-600 hover:text-red-800"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                          <div className="mt-2 flex gap-3 text-xs text-gray-600">
                            {level3Notes.length > 0 && (
                              <span>
                                <span className="font-medium">Advice:</span> {level3Notes.length}
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
                
                {/* Add new Level 3 diagnosis */}
                <div>
                  <Label>Add New Level 3 Diagnosis</Label>
                  <div className="flex gap-2 mt-2">
                    <Input
                      placeholder="e.g., Rhinovirus, Adenovirus"
                      value={newLevel3Diagnosis}
                      onChange={(e) => setNewLevel3Diagnosis(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          if (newLevel3Diagnosis.trim()) {
                            setDiagnosisForm(prev => {
                              const updated = [...prev.level_2_diagnosis];
                              const currentL2 = updated[managingLevel3ForDiagnosisIndex];
                              const l2Obj = typeof currentL2 === 'string' 
                                ? { name: currentL2, associated_notes: [], level_3_diagnosis: [] }
                                : { ...currentL2 };
                              
                              l2Obj.level_3_diagnosis = [...(l2Obj.level_3_diagnosis || []), { name: newLevel3Diagnosis.trim(), associated_notes: [] }];
                              updated[managingLevel3ForDiagnosisIndex] = l2Obj;
                              return { ...prev, level_2_diagnosis: updated };
                            });
                            setNewLevel3Diagnosis('');
                          }
                        }
                      }}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        if (newLevel3Diagnosis.trim()) {
                          setDiagnosisForm(prev => {
                            const updated = [...prev.level_2_diagnosis];
                            const currentL2 = updated[managingLevel3ForDiagnosisIndex];
                            const l2Obj = typeof currentL2 === 'string' 
                              ? { name: currentL2, associated_notes: [], level_3_diagnosis: [] }
                              : { ...currentL2 };
                            
                            l2Obj.level_3_diagnosis = [...(l2Obj.level_3_diagnosis || []), { name: newLevel3Diagnosis.trim(), associated_notes: [] }];
                            updated[managingLevel3ForDiagnosisIndex] = l2Obj;
                            return { ...prev, level_2_diagnosis: updated };
                          });
                          setNewLevel3Diagnosis('');
                        }
                      }}
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </>
            );
          })()}
          
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button
              type="button"
              onClick={() => {
                setManagingLevel3ForDiagnosisIndex(null);
                setNewLevel3Diagnosis('');
              }}
            >
              Done
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      )}

      {/* Level 2 Diagnosis Notes Dialog - Conditionally rendered for performance */}
      {showLevel2DiagnosisNotes && (
      <Dialog open={true} onOpenChange={setShowLevel2DiagnosisNotes}>
        <DialogContent className="max-w-2xl max-h-[600px] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Add Advice for Level 2 Diagnosis
              {editingLevel2DiagnosisIndex !== null && diagnosisForm.level_2_diagnosis[editingLevel2DiagnosisIndex] && (
                <span className="block text-sm font-normal text-gray-600 mt-1">
                  {typeof diagnosisForm.level_2_diagnosis[editingLevel2DiagnosisIndex] === 'string' 
                    ? diagnosisForm.level_2_diagnosis[editingLevel2DiagnosisIndex]
                    : diagnosisForm.level_2_diagnosis[editingLevel2DiagnosisIndex].name}
                </span>
              )}
            </DialogTitle>
          </DialogHeader>
          
          {editingLevel2DiagnosisIndex !== null && (
            <div className="space-y-4">
              {/* Notes Search */}
              <div>
                <Label>Search and Add Advice</Label>
                <Input
                  placeholder="Search notes..."
                  value={diagnosisForm.notesTemplateSearch || ''}
                  onChange={(e) => setDiagnosisForm(prev => ({ ...prev, notesTemplateSearch: e.target.value }))}
                />
              </div>

              {/* Available Notes */}
              <div className="max-h-60 overflow-y-auto border rounded p-2 space-y-1">
                {additionalNotesTemplates
                  .filter(notes => 
                    notes.note_title.toLowerCase().includes((diagnosisForm.notesTemplateSearch || '').toLowerCase())
                  )
                  .map(notes => {
                    const currentLevel2 = diagnosisForm.level_2_diagnosis[editingLevel2DiagnosisIndex];
                    const level2Notes = typeof currentLevel2 === 'object' ? currentLevel2.associated_notes || [] : [];
                    const isSelected = level2Notes.includes(notes.id);
                    
                    return (
                      <button
                        key={notes.id}
                        type="button"
                        onClick={() => {
                          setDiagnosisForm(prev => {
                            const updated = [...prev.level_2_diagnosis];
                            const currentL2 = updated[editingLevel2DiagnosisIndex];
                            const l2Obj = typeof currentL2 === 'string' 
                              ? { name: currentL2, associated_notes: [], level_3_diagnosis: [] }
                              : { ...currentL2 };
                            
                            if (isSelected) {
                              l2Obj.associated_notes = l2Obj.associated_notes.filter(id => id !== notes.id);
                            } else {
                              // Prevent duplicates
                              if (!l2Obj.associated_notes.includes(notes.id)) {
                                l2Obj.associated_notes = [...(l2Obj.associated_notes || []), notes.id];
                              }
                            }
                            
                            updated[editingLevel2DiagnosisIndex] = l2Obj;
                            return { ...prev, level_2_diagnosis: updated };
                          });
                        }}
                        className={`w-full text-left p-2 rounded border transition-colors ${
                          isSelected 
                            ? 'bg-teal-50 border-teal-500' 
                            : 'bg-white border-gray-200 hover:border-teal-300'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <div className={`w-4 h-4 rounded border flex items-center justify-center ${
                            isSelected ? 'bg-teal-500 border-teal-500' : 'border-gray-300'
                          }`}>
                            {isSelected && <span className="text-white text-xs">✓</span>}
                          </div>
                          <span className="text-sm">{notes.note_title}</span>
                        </div>
                      </button>
                    );
                  })}
              </div>

              {/* Selected Notes Count */}
              <div className="text-sm text-gray-600">
                {(() => {
                  const currentL2 = diagnosisForm.level_2_diagnosis[editingLevel2DiagnosisIndex];
                  const level2Notes = typeof currentL2 === 'object' ? currentL2.associated_notes || [] : [];
                  
                  // Only count advice items that actually exist in the templates
                  const validNotes = level2Notes.filter(noteId => 
                    additionalNotesTemplates.some(n => n.id === noteId)
                  );
                  
                  return `${validNotes.length} advice item(s) selected`;
                })()}
              </div>

              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button type="button" onClick={() => setShowLevel2DiagnosisNotes(false)}>
                  Done
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      )}

      {/* Level 3 Diagnosis Notes Dialog - Conditionally rendered for performance */}
      {showLevel3DiagnosisNotes && (
      <Dialog open={true} onOpenChange={setShowLevel3DiagnosisNotes}>
        <DialogContent className="max-w-2xl max-h-[600px] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Add Advice for Level 3 Diagnosis
              {managingLevel3ForDiagnosisIndex !== null && editingLevel3DiagnosisIndex !== null && (() => {
                const currentLevel2 = diagnosisForm.level_2_diagnosis[managingLevel3ForDiagnosisIndex];
                const level3List = typeof currentLevel2 === 'object' ? currentLevel2.level_3_diagnosis || [] : [];
                const level3 = level3List[editingLevel3DiagnosisIndex];
                
                return (
                  <span className="block text-sm font-normal text-gray-600 mt-1">
                    {typeof level3 === 'string' ? level3 : level3?.name || ''}
                  </span>
                );
              })()}
            </DialogTitle>
          </DialogHeader>
          
          {managingLevel3ForDiagnosisIndex !== null && editingLevel3DiagnosisIndex !== null && (
            <div className="space-y-4">
              {/* Notes Search */}
              <div>
                <Label>Search and Add Advice</Label>
                <Input
                  placeholder="Search notes..."
                  value={diagnosisForm.notesTemplateSearch || ''}
                  onChange={(e) => setDiagnosisForm(prev => ({ ...prev, notesTemplateSearch: e.target.value }))}
                />
              </div>

              {/* Available Notes */}
              <div className="max-h-60 overflow-y-auto border rounded p-2 space-y-1">
                {additionalNotesTemplates
                  .filter(notes => 
                    notes.note_title.toLowerCase().includes((diagnosisForm.notesTemplateSearch || '').toLowerCase())
                  )
                  .map(notes => {
                    const currentLevel2 = diagnosisForm.level_2_diagnosis[managingLevel3ForDiagnosisIndex];
                    const level3List = typeof currentLevel2 === 'object' ? currentLevel2.level_3_diagnosis || [] : [];
                    const currentLevel3 = level3List[editingLevel3DiagnosisIndex];
                    const level3Notes = typeof currentLevel3 === 'object' ? currentLevel3.associated_notes || [] : [];
                    const isSelected = level3Notes.includes(notes.id);
                    
                    return (
                      <button
                        key={notes.id}
                        type="button"
                        onClick={() => {
                          setDiagnosisForm(prev => {
                            const updated = [...prev.level_2_diagnosis];
                            const currentL2 = updated[managingLevel3ForDiagnosisIndex];
                            const l2Obj = typeof currentL2 === 'string' 
                              ? { name: currentL2, associated_notes: [], level_3_diagnosis: [] }
                              : { ...currentL2 };
                            
                            const level3List = l2Obj.level_3_diagnosis || [];
                            const currentL3 = level3List[editingLevel3DiagnosisIndex];
                            const l3Obj = typeof currentL3 === 'string'
                              ? { name: currentL3, associated_notes: [] }
                              : { ...currentL3 };
                            
                            if (isSelected) {
                              l3Obj.associated_notes = l3Obj.associated_notes.filter(id => id !== notes.id);
                            } else {
                              // Prevent duplicates
                              if (!l3Obj.associated_notes.includes(notes.id)) {
                                l3Obj.associated_notes = [...(l3Obj.associated_notes || []), notes.id];
                              }
                            }
                            
                            l2Obj.level_3_diagnosis[editingLevel3DiagnosisIndex] = l3Obj;
                            updated[managingLevel3ForDiagnosisIndex] = l2Obj;
                            return { ...prev, level_2_diagnosis: updated };
                          });
                        }}
                        className={`w-full text-left p-2 rounded border transition-colors ${
                          isSelected 
                            ? 'bg-teal-50 border-teal-500' 
                            : 'bg-white border-gray-200 hover:border-teal-300'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <div className={`w-4 h-4 rounded border flex items-center justify-center ${
                            isSelected ? 'bg-teal-500 border-teal-500' : 'border-gray-300'
                          }`}>
                            {isSelected && <span className="text-white text-xs">✓</span>}
                          </div>
                          <span className="text-sm">{notes.note_title}</span>
                        </div>
                      </button>
                    );
                  })}
              </div>

              {/* Selected Notes Count */}
              <div className="text-sm text-gray-600">
                {(() => {
                  const currentLevel2 = diagnosisForm.level_2_diagnosis[managingLevel3ForDiagnosisIndex];
                  const level3List = typeof currentLevel2 === 'object' ? currentLevel2.level_3_diagnosis || [] : [];
                  const currentLevel3 = level3List[editingLevel3DiagnosisIndex];
                  const level3Notes = typeof currentLevel3 === 'object' ? currentLevel3.associated_notes || [] : [];
                  
                  // Only count advice items that actually exist in the templates
                  const validNotes = level3Notes.filter(noteId => 
                    additionalNotesTemplates.some(n => n.id === noteId)
                  );
                  
                  return `${validNotes.length} advice item(s) selected`;
                })()}
              </div>

              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button type="button" onClick={() => setShowLevel3DiagnosisNotes(false)}>
                  Done
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      )}

      {/* Add/Edit Chief Complaint Dialog - Conditionally rendered for performance */}
      {(showAddChiefComplaint || showEditChiefComplaint) && (
        <Dialog open={true} onOpenChange={(open) => {
          if (!open) {
            setShowAddChiefComplaint(false);
            setShowEditChiefComplaint(false);
            setEditingChiefComplaint(null);
          }
        }}>
          <DialogContent className="max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingChiefComplaint ? 'Edit Chief Complaint Template' : 'Add Chief Complaint Template'}</DialogTitle>
              <DialogDescription>
                Create chief complaint templates with associated medications and tests for quick session entry
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={editingChiefComplaint ? handleEditChiefComplaint : handleAddChiefComplaint} className="space-y-4">
              <div>
                <Label>Chief Complaint Name *</Label>
                <Input
                  placeholder="e.g., Fever, Cough, Abdominal Pain"
                  value={chiefComplaintForm.complaint_name}
                  onChange={(e) => setChiefComplaintForm(prev => ({ ...prev, complaint_name: e.target.value }))}
                  required
                  autoFocus
                />
              </div>
              
              <div>
                <Label>Description (Optional)</Label>
                <Textarea
                  placeholder="Additional details about the chief complaint..."
                  value={chiefComplaintForm.description}
                  onChange={(e) => setChiefComplaintForm(prev => ({ ...prev, description: e.target.value }))}
                  rows={2}
                />
              </div>

              {/* Level 2 Complaints Section */}
              <div>
                <Label>Level 2 Complaints (Optional)</Label>
                <p className="text-xs text-gray-500 mb-2">Add specific variations with their own medications</p>
                
                {/* Display existing Level 2 complaints */}
              {chiefComplaintForm.sub_complaints.length > 0 && (
                <div className="space-y-2 mb-2">
                  {chiefComplaintForm.sub_complaints.map((subComplaint, index) => {
                    const subName = typeof subComplaint === 'string' ? subComplaint : subComplaint.name;
                    const subMeds = typeof subComplaint === 'object' ? subComplaint.associated_medications : [];
                    const subSubComplaints = typeof subComplaint === 'object' ? subComplaint.sub_sub_complaints || [] : [];
                    
                    return (
                      <div key={index} className="p-3 bg-blue-50 border border-blue-200 rounded">
                        <div className="flex items-start gap-2">
                          <span className="text-sm flex-1 font-medium text-gray-900">{subName}</span>
                          <button
                            type="button"
                            onClick={() => {
                              setManagingSubSubForIndex(index);
                            }}
                            className="text-purple-600 hover:text-purple-800 text-xs whitespace-nowrap"
                          >
                            + Level 3
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setEditingSubComplaintIndex(index);
                              setShowSubComplaintMedications(true);
                            }}
                            className="text-blue-600 hover:text-blue-800 text-xs"
                          >
                            + Meds
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setChiefComplaintForm(prev => ({
                                ...prev,
                                sub_complaints: prev.sub_complaints.filter((_, i) => i !== index)
                              }));
                            }}
                            className="text-red-600 hover:text-red-800"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                        <div className="mt-2 flex gap-3 text-xs text-gray-600">
                          {subMeds && subMeds.length > 0 && (
                            <span>
                              <span className="font-medium">Medications:</span> {subMeds.length}
                            </span>
                          )}
                          {subSubComplaints.length > 0 && (
                            <span>
                              <span className="font-medium">Level 3:</span> {subSubComplaints.length}
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              
              {/* Add new Level 2 complaint */}
              <div className="flex gap-2">
                <Input
                  placeholder="e.g., High fever (>102°F), Low-grade fever"
                  value={newSubComplaint}
                  onChange={(e) => setNewSubComplaint(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      if (newSubComplaint.trim()) {
                        setChiefComplaintForm(prev => ({
                          ...prev,
                          sub_complaints: [...prev.sub_complaints, { name: newSubComplaint.trim(), associated_medications: [], sub_sub_complaints: [] }]
                        }));
                        setNewSubComplaint('');
                      }
                    }
                  }}
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    if (newSubComplaint.trim()) {
                      setChiefComplaintForm(prev => ({
                        ...prev,
                        sub_complaints: [...prev.sub_complaints, { name: newSubComplaint.trim(), associated_medications: [], sub_sub_complaints: [] }]
                      }));
                      setNewSubComplaint('');
                    }
                  }}
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Associated Medications */}
            <div>
              <Label>Associated Medications (Optional)</Label>
              
              <div className="mt-2">
                <p className="text-xs font-medium text-gray-700 mb-2">Your Medication Templates</p>
                
                {/* Search for Medication Templates */}
                {(commonMedications.length > 0 || medicationTemplates.length > 0) && (
                  <div className="relative mb-2">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      placeholder="Search medication templates..."
                      value={chiefComplaintForm.medicationSearch || ''}
                      onChange={(e) => setChiefComplaintForm(prev => ({ ...prev, medicationSearch: e.target.value }))}
                      className="pl-10 text-sm"
                    />
                  </div>
                )}
                
                <div className="space-y-2 max-h-48 overflow-y-auto border rounded-lg p-3">
                  {commonMedications.length === 0 && medicationTemplates.length === 0 ? (
                    <p className="text-sm text-gray-500">No medication templates available. Create one first.</p>
                  ) : (
                    <>
                      {/* Common Medications */}
                      {commonMedications.length > 0 && (
                        <div>
                          <p className="text-xs font-semibold text-gray-600 mb-1">Common Medications</p>
                          {commonMedications
                            .filter(med => 
                              med.medication_name.toLowerCase().includes((chiefComplaintForm.medicationSearch || '').toLowerCase())
                            )
                            .map(med => (
                              <label key={`common-${med.id}`} className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 p-2 rounded">
                                <input
                                  type="checkbox"
                                  checked={chiefComplaintForm.associated_medications.includes(`common-${med.id}`)}
                                  onChange={() => {
                                    const medId = `common-${med.id}`;
                                    setChiefComplaintForm(prev => ({
                                      ...prev,
                                      associated_medications: prev.associated_medications.includes(medId)
                                        ? prev.associated_medications.filter(id => id !== medId)
                                        : [...prev.associated_medications, medId]
                                    }));
                                  }}
                                  className="w-4 h-4 text-teal-600"
                                />
                                <MedicationIcon type={med.medication_type} size={16} className="text-gray-600" />
                                <span className="text-sm">{med.medication_name}</span>
                              </label>
                            ))}
                        </div>
                      )}
                      
                      {/* Personal Medications */}
                      {medicationTemplates.length > 0 && (
                        <div className={commonMedications.length > 0 ? "mt-3" : ""}>
                          <p className="text-xs font-semibold text-gray-600 mb-1">Personal Medications</p>
                          {medicationTemplates
                            .filter(med => 
                              med.medication_name.toLowerCase().includes((chiefComplaintForm.medicationSearch || '').toLowerCase())
                            )
                            .map(med => (
                              <label key={med.id} className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 p-2 rounded">
                                <input
                                  type="checkbox"
                                  checked={chiefComplaintForm.associated_medications.includes(med.id)}
                                  onChange={() => {
                                    setChiefComplaintForm(prev => ({
                                      ...prev,
                                      associated_medications: prev.associated_medications.includes(med.id)
                                        ? prev.associated_medications.filter(id => id !== med.id)
                                        : [...prev.associated_medications, med.id]
                                    }));
                                  }}
                                  className="w-4 h-4 text-teal-600"
                                />
                                <MedicationIcon type={med.medication_type} size={16} className="text-gray-600" />
                                <span className="text-sm">{med.medication_name}</span>
                              </label>
                            ))}
                        </div>
                      )}
                      
                      {/* No results message */}
                      {(commonMedications.length > 0 || medicationTemplates.length > 0) &&
                       commonMedications.filter(med => 
                         med.medication_name.toLowerCase().includes((chiefComplaintForm.medicationSearch || '').toLowerCase())
                       ).length === 0 &&
                       medicationTemplates.filter(med => 
                         med.medication_name.toLowerCase().includes((chiefComplaintForm.medicationSearch || '').toLowerCase())
                       ).length === 0 && (
                        <p className="text-sm text-gray-500">No medications found matching your search.</p>
                      )}
                    </>
                  )}
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-1">These medications will be suggested when this chief complaint is selected</p>
            </div>

            {/* Associated Tests */}
            <div>
              <Label>Associated Tests (Optional)</Label>
              
              <div className="mt-2">
                <p className="text-xs font-medium text-gray-700 mb-2">Your Test Templates</p>
                
                {/* Search for Test Templates */}
                {testTemplates.length > 0 && (
                  <div className="relative mb-2">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      placeholder="Search test templates..."
                      value={chiefComplaintForm.testSearch || ''}
                      onChange={(e) => setChiefComplaintForm(prev => ({ ...prev, testSearch: e.target.value }))}
                      className="pl-10 text-sm"
                    />
                  </div>
                )}
                
                <div className="space-y-2 max-h-48 overflow-y-auto border rounded-lg p-3">
                  {testTemplates.length > 0 ? (
                    testTemplates
                      .filter(test => 
                        test.test_name.toLowerCase().includes((chiefComplaintForm.testSearch || '').toLowerCase())
                      )
                      .map(test => (
                        <label key={test.id} className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 p-2 rounded">
                          <input
                            type="checkbox"
                            checked={chiefComplaintForm.associated_tests.includes(test.id)}
                            onChange={() => {
                              setChiefComplaintForm(prev => ({
                                ...prev,
                                associated_tests: prev.associated_tests.includes(test.id)
                                  ? prev.associated_tests.filter(id => id !== test.id)
                                  : [...prev.associated_tests, test.id]
                              }));
                            }}
                            className="w-4 h-4 text-teal-600"
                          />
                          <span className="text-sm">{test.test_name}</span>
                        </label>
                      ))
                  ) : (
                    <p className="text-sm text-gray-500">No test templates available. Create one first.</p>
                  )}
                  {testTemplates.length > 0 && 
                   testTemplates.filter(test => 
                     test.test_name.toLowerCase().includes((chiefComplaintForm.testSearch || '').toLowerCase())
                   ).length === 0 && (
                    <p className="text-sm text-gray-500">No tests found matching your search.</p>
                  )}
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-1">These tests will be suggested when this chief complaint is selected</p>
            </div>
            
            <div className="flex space-x-2 pt-4">
              <Button type="submit" className="flex-1 bg-red-600 hover:bg-red-700">Save Template</Button>
              <Button type="button" variant="outline" onClick={() => {
                setShowAddChiefComplaint(false);
                setShowEditChiefComplaint(false);
                setEditingChiefComplaint(null);
              }} className="flex-1">Cancel</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
      )}

      {/* Level 2 Complaint Medications Dialog - Conditionally rendered */}
      {showSubComplaintMedications && (
        <Dialog open={true} onOpenChange={setShowSubComplaintMedications}>
          <DialogContent className="max-w-2xl max-h-[600px] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                Add Medications for Level 2 Complaint
                {editingSubComplaintIndex !== null && chiefComplaintForm.sub_complaints[editingSubComplaintIndex] && (
                  <span className="block text-sm font-normal text-gray-600 mt-1">
                    {typeof chiefComplaintForm.sub_complaints[editingSubComplaintIndex] === 'string' 
                      ? chiefComplaintForm.sub_complaints[editingSubComplaintIndex]
                      : chiefComplaintForm.sub_complaints[editingSubComplaintIndex].name}
                  </span>
                )}
              </DialogTitle>
            </DialogHeader>
            
            {editingSubComplaintIndex !== null && (
              <div className="space-y-4">
                {/* Medication Search */}
              <div>
                <Label>Search and Add Medications</Label>
                <Input
                  placeholder="Search medications..."
                  value={chiefComplaintForm.medicationSearch}
                  onChange={(e) => setChiefComplaintForm(prev => ({ ...prev, medicationSearch: e.target.value }))}
                />
              </div>

              {/* Available Medications */}
              <div className="max-h-60 overflow-y-auto border rounded p-2 space-y-1">
                {[...medicationTemplates, ...commonMedications.map(m => ({ ...m, isCommon: true }))]
                  .filter(m => {
                    const name = m.medication_name || m.name || '';
                    return name.toLowerCase().includes((chiefComplaintForm.medicationSearch || '').toLowerCase());
                  })
                  .map(med => {
                    const medId = med.isCommon ? `common-${med.id}` : med.id;
                    const medName = med.medication_name || med.name;
                    const currentSubComplaint = chiefComplaintForm.sub_complaints[editingSubComplaintIndex];
                    const subMeds = typeof currentSubComplaint === 'object' ? currentSubComplaint.associated_medications || [] : [];
                    const isSelected = subMeds.includes(medId);
                    
                    return (
                      <button
                        key={medId}
                        type="button"
                        onClick={() => {
                          setChiefComplaintForm(prev => {
                            const updatedSubComplaints = [...prev.sub_complaints];
                            const currentSub = updatedSubComplaints[editingSubComplaintIndex];
                            const subObj = typeof currentSub === 'string' 
                              ? { name: currentSub, associated_medications: [] }
                              : { ...currentSub };
                            
                            if (isSelected) {
                              subObj.associated_medications = subObj.associated_medications.filter(id => id !== medId);
                            } else {
                              subObj.associated_medications = [...(subObj.associated_medications || []), medId];
                            }
                            
                            updatedSubComplaints[editingSubComplaintIndex] = subObj;
                            return { ...prev, sub_complaints: updatedSubComplaints };
                          });
                        }}
                        className={`w-full text-left p-2 rounded border transition-colors ${
                          isSelected 
                            ? 'bg-blue-50 border-blue-500' 
                            : 'bg-white border-gray-200 hover:border-blue-300'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <div className={`w-4 h-4 rounded border flex items-center justify-center ${
                            isSelected ? 'bg-blue-500 border-blue-500' : 'border-gray-300'
                          }`}>
                            {isSelected && <span className="text-white text-xs">✓</span>}
                          </div>
                          <span className="text-sm">{medName}</span>
                          {med.isCommon && <span className="text-xs text-gray-500">(Common)</span>}
                        </div>
                      </button>
                    );
                  })}
              </div>

              {/* Selected Medications Count */}
              <div className="text-sm text-gray-600">
                {(() => {
                  const currentSub = chiefComplaintForm.sub_complaints[editingSubComplaintIndex];
                  const subMeds = typeof currentSub === 'object' ? currentSub.associated_medications || [] : [];
                  return `${subMeds.length} medication(s) selected`;
                })()}
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowSubComplaintMedications(false);
                    setEditingSubComplaintIndex(null);
                  }}
                >
                  Done
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      )}

      {/* Level 3 Complaints Management Dialog - Conditionally rendered */}
      {managingSubSubForIndex !== null && (
        <Dialog open={true} onOpenChange={(open) => {
          if (!open) {
            setManagingSubSubForIndex(null);
            setNewSubSubComplaint('');
            setEditingSubSubComplaintIndex(null);
            setShowSubSubComplaintMedications(false);
          }
        }}>
          <DialogContent className="max-w-3xl max-h-[700px] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                Manage Level 3 Complaints
                {managingSubSubForIndex !== null && chiefComplaintForm.sub_complaints[managingSubSubForIndex] && (
                  <span className="block text-sm font-normal text-gray-600 mt-1">
                    For: {typeof chiefComplaintForm.sub_complaints[managingSubSubForIndex] === 'string' 
                      ? chiefComplaintForm.sub_complaints[managingSubSubForIndex]
                      : chiefComplaintForm.sub_complaints[managingSubSubForIndex].name}
                  </span>
                )}
              </DialogTitle>
            </DialogHeader>
            
            {managingSubSubForIndex !== null && (
              <div className="space-y-4">
              {/* Display existing sub-sub complaints */}
              {(() => {
                const currentSubComplaint = chiefComplaintForm.sub_complaints[managingSubSubForIndex];
                const subSubComplaints = typeof currentSubComplaint === 'object' ? currentSubComplaint.sub_sub_complaints || [] : [];
                
                return (
                  <>
                    {subSubComplaints.length > 0 && (
                      <div className="space-y-2 mb-4">
                        <Label>Existing Level 3 Complaints</Label>
                        {subSubComplaints.map((subSubComplaint, index) => {
                          const subSubName = typeof subSubComplaint === 'string' ? subSubComplaint : subSubComplaint.name;
                          const subSubMeds = typeof subSubComplaint === 'object' ? subSubComplaint.associated_medications || [] : [];
                          
                          const level4Complaints = typeof subSubComplaint === 'object' ? subSubComplaint.level_4_complaints || [] : [];
                          
                          return (
                            <div key={index} className="p-3 bg-purple-50 border border-purple-200 rounded">
                              <div className="flex items-start gap-2">
                                <span className="text-sm flex-1 font-medium text-gray-900">{subSubName}</span>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setManagingLevel4ForIndex(index);
                                    setManagingLevel4ForSubIndex(managingSubSubForIndex);
                                  }}
                                  className="text-orange-600 hover:text-orange-800 text-xs whitespace-nowrap"
                                >
                                  + Level 4
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setEditingSubSubComplaintIndex(index);
                                    setShowSubSubComplaintMedications(true);
                                  }}
                                  className="text-blue-600 hover:text-blue-800 text-xs"
                                >
                                  + Meds
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setChiefComplaintForm(prev => {
                                      const updatedSubComplaints = [...prev.sub_complaints];
                                      const currentSub = updatedSubComplaints[managingSubSubForIndex];
                                      const subObj = typeof currentSub === 'string' 
                                        ? { name: currentSub, associated_medications: [], sub_sub_complaints: [] }
                                        : { ...currentSub };
                                      
                                      subObj.sub_sub_complaints = (subObj.sub_sub_complaints || []).filter((_, i) => i !== index);
                                      updatedSubComplaints[managingSubSubForIndex] = subObj;
                                      return { ...prev, sub_complaints: updatedSubComplaints };
                                    });
                                  }}
                                  className="text-red-600 hover:text-red-800"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              </div>
                              <div className="mt-2 flex gap-3 text-xs text-gray-600">
                                {subSubMeds && subSubMeds.length > 0 && (
                                  <span>
                                    <span className="font-medium">Medications:</span> {subSubMeds.length}
                                  </span>
                                )}
                                {level4Complaints.length > 0 && (
                                  <span>
                                    <span className="font-medium">Level 4:</span> {level4Complaints.length}
                                  </span>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                    
                    {/* Add new sub-sub complaint */}
                    <div>
                      <Label>Add New Level 3 Complaint</Label>
                      <div className="flex gap-2 mt-2">
                        <Input
                          placeholder="e.g., Morning fever, Evening fever"
                          value={newSubSubComplaint}
                          onChange={(e) => setNewSubSubComplaint(e.target.value)}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              if (newSubSubComplaint.trim()) {
                                setChiefComplaintForm(prev => {
                                  const updatedSubComplaints = [...prev.sub_complaints];
                                  const currentSub = updatedSubComplaints[managingSubSubForIndex];
                                  const subObj = typeof currentSub === 'string' 
                                    ? { name: currentSub, associated_medications: [], sub_sub_complaints: [] }
                                    : { ...currentSub };
                                  
                                  subObj.sub_sub_complaints = [...(subObj.sub_sub_complaints || []), { name: newSubSubComplaint.trim(), associated_medications: [], level_4_complaints: [] }];
                                  updatedSubComplaints[managingSubSubForIndex] = subObj;
                                  return { ...prev, sub_complaints: updatedSubComplaints };
                                });
                                setNewSubSubComplaint('');
                              }
                            }
                          }}
                        />
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => {
                            if (newSubSubComplaint.trim()) {
                              setChiefComplaintForm(prev => {
                                const updatedSubComplaints = [...prev.sub_complaints];
                                const currentSub = updatedSubComplaints[managingSubSubForIndex];
                                const subObj = typeof currentSub === 'string' 
                                  ? { name: currentSub, associated_medications: [], sub_sub_complaints: [] }
                                  : { ...currentSub };
                                
                                subObj.sub_sub_complaints = [...(subObj.sub_sub_complaints || []), { name: newSubSubComplaint.trim(), associated_medications: [] }];
                                updatedSubComplaints[managingSubSubForIndex] = subObj;
                                return { ...prev, sub_complaints: updatedSubComplaints };
                              });
                              setNewSubSubComplaint('');
                            }
                          }}
                        >
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </>
                );
              })()}
              
              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button
                  type="button"
                  onClick={() => {
                    setManagingSubSubForIndex(null);
                    setNewSubSubComplaint('');
                  }}
                >
                  Done
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      )}

      {/* Level 3 Complaint Medications Dialog - Conditionally rendered for performance */}
      {showSubSubComplaintMedications && (
      <Dialog open={true} onOpenChange={setShowSubSubComplaintMedications}>
        <DialogContent className="max-w-2xl max-h-[600px] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Add Medications for Level 3 Complaint
              {managingSubSubForIndex !== null && editingSubSubComplaintIndex !== null && (() => {
                const currentSubComplaint = chiefComplaintForm.sub_complaints[managingSubSubForIndex];
                const subSubComplaints = typeof currentSubComplaint === 'object' ? currentSubComplaint.sub_sub_complaints || [] : [];
                const subSubComplaint = subSubComplaints[editingSubSubComplaintIndex];
                
                return (
                  <span className="block text-sm font-normal text-gray-600 mt-1">
                    {typeof subSubComplaint === 'string' ? subSubComplaint : subSubComplaint?.name || ''}
                  </span>
                );
              })()}
            </DialogTitle>
          </DialogHeader>
          
          {managingSubSubForIndex !== null && editingSubSubComplaintIndex !== null && (
            <div className="space-y-4">
              {/* Medication Search */}
              <div>
                <Label>Search and Add Medications</Label>
                <Input
                  placeholder="Search medications..."
                  value={chiefComplaintForm.medicationSearch}
                  onChange={(e) => setChiefComplaintForm(prev => ({ ...prev, medicationSearch: e.target.value }))}
                />
              </div>

              {/* Available Medications */}
              <div className="max-h-60 overflow-y-auto border rounded p-2 space-y-1">
                {[...medicationTemplates, ...commonMedications.map(m => ({ ...m, isCommon: true }))]
                  .filter(m => {
                    const name = m.medication_name || m.name || '';
                    return name.toLowerCase().includes((chiefComplaintForm.medicationSearch || '').toLowerCase());
                  })
                  .map(med => {
                    const medId = med.isCommon ? `common-${med.id}` : med.id;
                    const medName = med.medication_name || med.name;
                    
                    const currentSubComplaint = chiefComplaintForm.sub_complaints[managingSubSubForIndex];
                    const subSubComplaints = typeof currentSubComplaint === 'object' ? currentSubComplaint.sub_sub_complaints || [] : [];
                    const currentSubSubComplaint = subSubComplaints[editingSubSubComplaintIndex];
                    const subSubMeds = typeof currentSubSubComplaint === 'object' ? currentSubSubComplaint.associated_medications || [] : [];
                    const isSelected = subSubMeds.includes(medId);
                    
                    return (
                      <button
                        key={medId}
                        type="button"
                        onClick={() => {
                          setChiefComplaintForm(prev => {
                            const updatedSubComplaints = [...prev.sub_complaints];
                            const currentSub = updatedSubComplaints[managingSubSubForIndex];
                            const subObj = typeof currentSub === 'string' 
                              ? { name: currentSub, associated_medications: [], sub_sub_complaints: [] }
                              : { ...currentSub };
                            
                            const subSubArray = [...(subObj.sub_sub_complaints || [])];
                            const currentSubSub = subSubArray[editingSubSubComplaintIndex];
                            const subSubObj = typeof currentSubSub === 'string'
                              ? { name: currentSubSub, associated_medications: [] }
                              : { ...currentSubSub };
                            
                            if (isSelected) {
                              subSubObj.associated_medications = subSubObj.associated_medications.filter(id => id !== medId);
                            } else {
                              subSubObj.associated_medications = [...(subSubObj.associated_medications || []), medId];
                            }
                            
                            subSubArray[editingSubSubComplaintIndex] = subSubObj;
                            subObj.sub_sub_complaints = subSubArray;
                            updatedSubComplaints[managingSubSubForIndex] = subObj;
                            return { ...prev, sub_complaints: updatedSubComplaints };
                          });
                        }}
                        className={`w-full text-left p-2 rounded border transition-colors ${
                          isSelected 
                            ? 'bg-blue-50 border-blue-500' 
                            : 'bg-white border-gray-200 hover:border-blue-300'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <div className={`w-4 h-4 rounded border flex items-center justify-center ${
                            isSelected ? 'bg-blue-500 border-blue-500' : 'border-gray-300'
                          }`}>
                            {isSelected && <span className="text-white text-xs">✓</span>}
                          </div>
                          <span className="text-sm">{medName}</span>
                          {med.isCommon && <span className="text-xs text-gray-500">(Common)</span>}
                        </div>
                      </button>
                    );
                  })}
              </div>

              {/* Selected Medications Count */}
              <div className="text-sm text-gray-600">
                {(() => {
                  const currentSubComplaint = chiefComplaintForm.sub_complaints[managingSubSubForIndex];
                  const subSubComplaints = typeof currentSubComplaint === 'object' ? currentSubComplaint.sub_sub_complaints || [] : [];
                  const currentSubSubComplaint = subSubComplaints[editingSubSubComplaintIndex];
                  const subSubMeds = typeof currentSubSubComplaint === 'object' ? currentSubSubComplaint.associated_medications || [] : [];
                  return `${subSubMeds.length} medication(s) selected`;
                })()}
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowSubSubComplaintMedications(false);
                    setEditingSubSubComplaintIndex(null);
                  }}
                >
                  Done
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      )}

      {/* Level 4 Complaints Management Dialog - Conditionally rendered for performance */}
      {managingLevel4ForIndex !== null && (
      <Dialog open={true} onOpenChange={(open) => {
        if (!open) {
          setManagingLevel4ForIndex(null);
          setManagingLevel4ForSubIndex(null);
          setNewLevel4Complaint('');
          setEditingLevel4Index(null);
          setShowLevel4Medications(false);
        }
      }}>
        <DialogContent className="max-w-3xl max-h-[700px] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Manage Level 4 Complaints
              {managingLevel4ForIndex !== null && managingLevel4ForSubIndex !== null && (() => {
                const currentSubComplaint = chiefComplaintForm.sub_complaints[managingLevel4ForSubIndex];
                const subSubComplaints = typeof currentSubComplaint === 'object' ? currentSubComplaint.sub_sub_complaints || [] : [];
                const currentSubSub = subSubComplaints[managingLevel4ForIndex];
                
                return (
                  <span className="block text-sm font-normal text-gray-600 mt-1">
                    For: {typeof currentSubSub === 'string' ? currentSubSub : currentSubSub?.name || ''}
                  </span>
                );
              })()}
            </DialogTitle>
          </DialogHeader>
          
          {managingLevel4ForIndex !== null && managingLevel4ForSubIndex !== null && (
            <div className="space-y-4">
              {/* Display existing Level 4 complaints */}
              {(() => {
                const currentSubComplaint = chiefComplaintForm.sub_complaints[managingLevel4ForSubIndex];
                const subSubComplaints = typeof currentSubComplaint === 'object' ? currentSubComplaint.sub_sub_complaints || [] : [];
                const currentSubSub = subSubComplaints[managingLevel4ForIndex];
                const level4Complaints = typeof currentSubSub === 'object' ? currentSubSub.level_4_complaints || [] : [];
                
                return (
                  <>
                    {level4Complaints.length > 0 && (
                      <div className="space-y-2 mb-4">
                        <Label>Existing Level 4 Complaints</Label>
                        {level4Complaints.map((level4Complaint, index) => {
                          const level4Name = typeof level4Complaint === 'string' ? level4Complaint : level4Complaint.name;
                          const level4Meds = typeof level4Complaint === 'object' ? level4Complaint.associated_medications || [] : [];
                          
                          const level5Complaints = typeof level4Complaint === 'object' ? level4Complaint.level_5_complaints || [] : [];
                          
                          return (
                            <div key={index} className="p-3 bg-orange-50 border border-orange-200 rounded">
                              <div className="flex items-start gap-2">
                                <span className="text-sm flex-1 font-medium text-gray-900">{level4Name}</span>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setManagingLevel5ForIndex(index);
                                  }}
                                  className="text-teal-600 hover:text-teal-800 text-xs whitespace-nowrap"
                                >
                                  + Level 5
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setEditingLevel4Index(index);
                                    setShowLevel4Medications(true);
                                  }}
                                  className="text-blue-600 hover:text-blue-800 text-xs"
                                >
                                  + Meds
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setChiefComplaintForm(prev => {
                                      const updatedSubComplaints = [...prev.sub_complaints];
                                      const currentSub = updatedSubComplaints[managingLevel4ForSubIndex];
                                      const subObj = typeof currentSub === 'string' 
                                        ? { name: currentSub, associated_medications: [], sub_sub_complaints: [] }
                                        : { ...currentSub };
                                      
                                      const subSubArray = [...(subObj.sub_sub_complaints || [])];
                                      const currentSubSub = subSubArray[managingLevel4ForIndex];
                                      const subSubObj = typeof currentSubSub === 'string'
                                        ? { name: currentSubSub, associated_medications: [], level_4_complaints: [] }
                                        : { ...currentSubSub };
                                      
                                      subSubObj.level_4_complaints = (subSubObj.level_4_complaints || []).filter((_, i) => i !== index);
                                      subSubArray[managingLevel4ForIndex] = subSubObj;
                                      subObj.sub_sub_complaints = subSubArray;
                                      updatedSubComplaints[managingLevel4ForSubIndex] = subObj;
                                      return { ...prev, sub_complaints: updatedSubComplaints };
                                    });
                                  }}
                                  className="text-red-600 hover:text-red-800"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              </div>
                              <div className="mt-2 flex gap-3 text-xs text-gray-600">
                                {level4Meds && level4Meds.length > 0 && (
                                  <span>
                                    <span className="font-medium">Medications:</span> {level4Meds.length}
                                  </span>
                                )}
                                {level5Complaints.length > 0 && (
                                  <span>
                                    <span className="font-medium">Level 5:</span> {level5Complaints.length}
                                  </span>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                    
                    {/* Add new Level 4 complaint */}
                    <div>
                      <Label>Add New Level 4 Complaint</Label>
                      <div className="flex gap-2 mt-2">
                        <Input
                          placeholder="e.g., Persistent fever, Intermittent fever"
                          value={newLevel4Complaint}
                          onChange={(e) => setNewLevel4Complaint(e.target.value)}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              if (newLevel4Complaint.trim()) {
                                setChiefComplaintForm(prev => {
                                  const updatedSubComplaints = [...prev.sub_complaints];
                                  const currentSub = updatedSubComplaints[managingLevel4ForSubIndex];
                                  const subObj = typeof currentSub === 'string' 
                                    ? { name: currentSub, associated_medications: [], sub_sub_complaints: [] }
                                    : { ...currentSub };
                                  
                                  const subSubArray = [...(subObj.sub_sub_complaints || [])];
                                  const currentSubSub = subSubArray[managingLevel4ForIndex];
                                  const subSubObj = typeof currentSubSub === 'string'
                                    ? { name: currentSubSub, associated_medications: [], level_4_complaints: [] }
                                    : { ...currentSubSub };
                                  
                                  subSubObj.level_4_complaints = [...(subSubObj.level_4_complaints || []), { name: newLevel4Complaint.trim(), associated_medications: [], level_5_complaints: [] }];
                                  subSubArray[managingLevel4ForIndex] = subSubObj;
                                  subObj.sub_sub_complaints = subSubArray;
                                  updatedSubComplaints[managingLevel4ForSubIndex] = subObj;
                                  return { ...prev, sub_complaints: updatedSubComplaints };
                                });
                                setNewLevel4Complaint('');
                              }
                            }
                          }}
                        />
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => {
                            if (newLevel4Complaint.trim()) {
                              setChiefComplaintForm(prev => {
                                const updatedSubComplaints = [...prev.sub_complaints];
                                const currentSub = updatedSubComplaints[managingLevel4ForSubIndex];
                                const subObj = typeof currentSub === 'string' 
                                  ? { name: currentSub, associated_medications: [], sub_sub_complaints: [] }
                                  : { ...currentSub };
                                
                                const subSubArray = [...(subObj.sub_sub_complaints || [])];
                                const currentSubSub = subSubArray[managingLevel4ForIndex];
                                const subSubObj = typeof currentSubSub === 'string'
                                  ? { name: currentSubSub, associated_medications: [], level_4_complaints: [] }
                                  : { ...currentSubSub };
                                
                                subSubObj.level_4_complaints = [...(subSubObj.level_4_complaints || []), { name: newLevel4Complaint.trim(), associated_medications: [], level_5_complaints: [] }];
                                subSubArray[managingLevel4ForIndex] = subSubObj;
                                subObj.sub_sub_complaints = subSubArray;
                                updatedSubComplaints[managingLevel4ForSubIndex] = subObj;
                                return { ...prev, sub_complaints: updatedSubComplaints };
                              });
                              setNewLevel4Complaint('');
                            }
                          }}
                        >
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </>
                );
              })()}
              
              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button
                  type="button"
                  onClick={() => {
                    // Close all parent levels at once for better UX
                    setManagingLevel4ForIndex(null);
                    setManagingLevel4ForSubIndex(null);
                    setManagingLevel5ForIndex(null);
                    setManagingLevel6ForIndex(null);
                    setNewLevel4Complaint('');
                    setNewLevel5Complaint('');
                    setNewLevel6Complaint('');
                  }}
                >
                  Done
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      )}

      {/* Level 4 Complaint Medications Dialog - Conditionally rendered for performance */}
      {showLevel4Medications && (
      <Dialog open={true} onOpenChange={setShowLevel4Medications}>
        <DialogContent className="max-w-2xl max-h-[600px] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Add Medications for Level 4 Complaint
              {managingLevel4ForIndex !== null && managingLevel4ForSubIndex !== null && editingLevel4Index !== null && (() => {
                const currentSubComplaint = chiefComplaintForm.sub_complaints[managingLevel4ForSubIndex];
                const subSubComplaints = typeof currentSubComplaint === 'object' ? currentSubComplaint.sub_sub_complaints || [] : [];
                const currentSubSub = subSubComplaints[managingLevel4ForIndex];
                const level4Complaints = typeof currentSubSub === 'object' ? currentSubSub.level_4_complaints || [] : [];
                const level4Complaint = level4Complaints[editingLevel4Index];
                
                return (
                  <span className="block text-sm font-normal text-gray-600 mt-1">
                    {typeof level4Complaint === 'string' ? level4Complaint : level4Complaint?.name || ''}
                  </span>
                );
              })()}
            </DialogTitle>
          </DialogHeader>
          
          {managingLevel4ForIndex !== null && managingLevel4ForSubIndex !== null && editingLevel4Index !== null && (
            <div className="space-y-4">
              {/* Medication Search */}
              <div>
                <Label>Search and Add Medications</Label>
                <Input
                  placeholder="Search medications..."
                  value={chiefComplaintForm.medicationSearch}
                  onChange={(e) => setChiefComplaintForm(prev => ({ ...prev, medicationSearch: e.target.value }))}
                />
              </div>

              {/* Available Medications */}
              <div className="max-h-60 overflow-y-auto border rounded p-2 space-y-1">
                {[...medicationTemplates, ...commonMedications.map(m => ({ ...m, isCommon: true }))]
                  .filter(m => {
                    const name = m.medication_name || m.name || '';
                    return name.toLowerCase().includes((chiefComplaintForm.medicationSearch || '').toLowerCase());
                  })
                  .map(med => {
                    const medId = med.isCommon ? `common-${med.id}` : med.id;
                    const medName = med.medication_name || med.name;
                    
                    const currentSubComplaint = chiefComplaintForm.sub_complaints[managingLevel4ForSubIndex];
                    const subSubComplaints = typeof currentSubComplaint === 'object' ? currentSubComplaint.sub_sub_complaints || [] : [];
                    const currentSubSub = subSubComplaints[managingLevel4ForIndex];
                    const level4Complaints = typeof currentSubSub === 'object' ? currentSubSub.level_4_complaints || [] : [];
                    const currentLevel4 = level4Complaints[editingLevel4Index];
                    const level4Meds = typeof currentLevel4 === 'object' ? currentLevel4.associated_medications || [] : [];
                    const isSelected = level4Meds.includes(medId);
                    
                    return (
                      <button
                        key={medId}
                        type="button"
                        onClick={() => {
                          setChiefComplaintForm(prev => {
                            const updatedSubComplaints = [...prev.sub_complaints];
                            const currentSub = updatedSubComplaints[managingLevel4ForSubIndex];
                            const subObj = typeof currentSub === 'string' 
                              ? { name: currentSub, associated_medications: [], sub_sub_complaints: [] }
                              : { ...currentSub };
                            
                            const subSubArray = [...(subObj.sub_sub_complaints || [])];
                            const currentSubSub = subSubArray[managingLevel4ForIndex];
                            const subSubObj = typeof currentSubSub === 'string'
                              ? { name: currentSubSub, associated_medications: [], level_4_complaints: [] }
                              : { ...currentSubSub };
                            
                            const level4Array = [...(subSubObj.level_4_complaints || [])];
                            const currentLevel4 = level4Array[editingLevel4Index];
                            const level4Obj = typeof currentLevel4 === 'string'
                              ? { name: currentLevel4, associated_medications: [] }
                              : { ...currentLevel4 };
                            
                            if (isSelected) {
                              level4Obj.associated_medications = level4Obj.associated_medications.filter(id => id !== medId);
                            } else {
                              level4Obj.associated_medications = [...(level4Obj.associated_medications || []), medId];
                            }
                            
                            level4Array[editingLevel4Index] = level4Obj;
                            subSubObj.level_4_complaints = level4Array;
                            subSubArray[managingLevel4ForIndex] = subSubObj;
                            subObj.sub_sub_complaints = subSubArray;
                            updatedSubComplaints[managingLevel4ForSubIndex] = subObj;
                            return { ...prev, sub_complaints: updatedSubComplaints };
                          });
                        }}
                        className={`w-full text-left p-2 rounded border transition-colors ${
                          isSelected 
                            ? 'bg-blue-50 border-blue-500' 
                            : 'bg-white border-gray-200 hover:border-blue-300'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <div className={`w-4 h-4 rounded border flex items-center justify-center ${
                            isSelected ? 'bg-blue-500 border-blue-500' : 'border-gray-300'
                          }`}>
                            {isSelected && <span className="text-white text-xs">✓</span>}
                          </div>
                          <span className="text-sm">{medName}</span>
                          {med.isCommon && <span className="text-xs text-gray-500">(Common)</span>}
                        </div>
                      </button>
                    );
                  })}
              </div>

              {/* Selected Medications Count */}
              <div className="text-sm text-gray-600">
                {(() => {
                  const currentSubComplaint = chiefComplaintForm.sub_complaints[managingLevel4ForSubIndex];
                  const subSubComplaints = typeof currentSubComplaint === 'object' ? currentSubComplaint.sub_sub_complaints || [] : [];
                  const currentSubSub = subSubComplaints[managingLevel4ForIndex];
                  const level4Complaints = typeof currentSubSub === 'object' ? currentSubSub.level_4_complaints || [] : [];
                  const currentLevel4 = level4Complaints[editingLevel4Index];
                  const level4Meds = typeof currentLevel4 === 'object' ? currentLevel4.associated_medications || [] : [];
                  return `${level4Meds.length} medication(s) selected`;
                })()}
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowLevel4Medications(false);
                    setEditingLevel4Index(null);
                  }}
                >
                  Done
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      )}

      {/* Level 5 Complaints Management Dialog - Conditionally rendered for performance */}
      {managingLevel5ForIndex !== null && (
      <Dialog open={true} onOpenChange={(open) => {
        if (!open) {
          setManagingLevel5ForIndex(null);
          setNewLevel5Complaint('');
          setEditingLevel5Index(null);
          setShowLevel5Medications(false);
        }
      }}>
        <DialogContent className="max-w-3xl max-h-[700px] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Manage Level 5 Complaints
              {managingLevel5ForIndex !== null && managingLevel4ForIndex !== null && managingLevel4ForSubIndex !== null && (() => {
                const currentSubComplaint = chiefComplaintForm.sub_complaints[managingLevel4ForSubIndex];
                const subSubComplaints = typeof currentSubComplaint === 'object' ? currentSubComplaint.sub_sub_complaints || [] : [];
                const currentSubSub = subSubComplaints[managingLevel4ForIndex];
                const level4Complaints = typeof currentSubSub === 'object' ? currentSubSub.level_4_complaints || [] : [];
                const currentLevel4 = level4Complaints[managingLevel5ForIndex];
                
                return (
                  <span className="block text-sm font-normal text-gray-600 mt-1">
                    For: {typeof currentLevel4 === 'string' ? currentLevel4 : currentLevel4?.name || ''}
                  </span>
                );
              })()}
            </DialogTitle>
          </DialogHeader>
          
          {managingLevel5ForIndex !== null && (
            <div className="space-y-4">
              {(() => {
                if (managingLevel4ForIndex === null || managingLevel4ForSubIndex === null) return null;
                
                const currentSubComplaint = chiefComplaintForm.sub_complaints[managingLevel4ForSubIndex];
                const subSubComplaints = typeof currentSubComplaint === 'object' ? currentSubComplaint.sub_sub_complaints || [] : [];
                const currentSubSub = subSubComplaints[managingLevel4ForIndex];
                const level4Complaints = typeof currentSubSub === 'object' ? currentSubSub.level_4_complaints || [] : [];
                const currentLevel4 = level4Complaints[managingLevel5ForIndex];
                const level5Complaints = typeof currentLevel4 === 'object' ? currentLevel4.level_5_complaints || [] : [];
                
                return (
                  <>
                    {level5Complaints.length > 0 && (
                      <div className="space-y-2 mb-4">
                        <Label>Existing Level 5 Complaints</Label>
                        {level5Complaints.map((level5Complaint, index) => {
                          const level5Name = typeof level5Complaint === 'string' ? level5Complaint : level5Complaint.name;
                          const level5Meds = typeof level5Complaint === 'object' ? level5Complaint.associated_medications || [] : [];
                          const level6Complaints = typeof level5Complaint === 'object' ? level5Complaint.level_6_complaints || [] : [];
                          
                          return (
                            <div key={index} className="p-3 bg-teal-50 border border-teal-200 rounded">
                              <div className="flex items-start gap-2">
                                <span className="text-sm flex-1 font-medium text-gray-900">{level5Name}</span>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setManagingLevel6ForIndex(index);
                                  }}
                                  className="text-pink-600 hover:text-pink-800 text-xs whitespace-nowrap"
                                >
                                  + Level 6
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setEditingLevel5Index(index);
                                    setShowLevel5Medications(true);
                                  }}
                                  className="text-blue-600 hover:text-blue-800 text-xs"
                                >
                                  + Meds
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setChiefComplaintForm(prev => {
                                      const updatedSubComplaints = [...prev.sub_complaints];
                                      const currentSub = updatedSubComplaints[managingLevel4ForSubIndex];
                                      const subObj = typeof currentSub === 'string' ? { name: currentSub, associated_medications: [], sub_sub_complaints: [] } : { ...currentSub };
                                      const subSubArray = [...(subObj.sub_sub_complaints || [])];
                                      const currentSubSub = subSubArray[managingLevel4ForIndex];
                                      const subSubObj = typeof currentSubSub === 'string' ? { name: currentSubSub, associated_medications: [], level_4_complaints: [] } : { ...currentSubSub };
                                      const level4Array = [...(subSubObj.level_4_complaints || [])];
                                      const currentLevel4 = level4Array[managingLevel5ForIndex];
                                      const level4Obj = typeof currentLevel4 === 'string' ? { name: currentLevel4, associated_medications: [], level_5_complaints: [] } : { ...currentLevel4 };
                                      
                                      level4Obj.level_5_complaints = (level4Obj.level_5_complaints || []).filter((_, i) => i !== index);
                                      level4Array[managingLevel5ForIndex] = level4Obj;
                                      subSubObj.level_4_complaints = level4Array;
                                      subSubArray[managingLevel4ForIndex] = subSubObj;
                                      subObj.sub_sub_complaints = subSubArray;
                                      updatedSubComplaints[managingLevel4ForSubIndex] = subObj;
                                      return { ...prev, sub_complaints: updatedSubComplaints };
                                    });
                                  }}
                                  className="text-red-600 hover:text-red-800"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              </div>
                              <div className="mt-2 flex gap-3 text-xs text-gray-600">
                                {level5Meds && level5Meds.length > 0 && (
                                  <span><span className="font-medium">Medications:</span> {level5Meds.length}</span>
                                )}
                                {level6Complaints.length > 0 && (
                                  <span><span className="font-medium">Level 6:</span> {level6Complaints.length}</span>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                    
                    <div>
                      <Label>Add New Level 5 Complaint</Label>
                      <div className="flex gap-2 mt-2">
                        <Input
                          placeholder="e.g., Acute, Chronic"
                          value={newLevel5Complaint}
                          onChange={(e) => setNewLevel5Complaint(e.target.value)}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              if (newLevel5Complaint.trim()) {
                                setChiefComplaintForm(prev => {
                                  const updatedSubComplaints = [...prev.sub_complaints];
                                  const currentSub = updatedSubComplaints[managingLevel4ForSubIndex];
                                  const subObj = typeof currentSub === 'string' ? { name: currentSub, associated_medications: [], sub_sub_complaints: [] } : { ...currentSub };
                                  const subSubArray = [...(subObj.sub_sub_complaints || [])];
                                  const currentSubSub = subSubArray[managingLevel4ForIndex];
                                  const subSubObj = typeof currentSubSub === 'string' ? { name: currentSubSub, associated_medications: [], level_4_complaints: [] } : { ...currentSubSub };
                                  const level4Array = [...(subSubObj.level_4_complaints || [])];
                                  const currentLevel4 = level4Array[managingLevel5ForIndex];
                                  const level4Obj = typeof currentLevel4 === 'string' ? { name: currentLevel4, associated_medications: [], level_5_complaints: [] } : { ...currentLevel4 };
                                  
                                  level4Obj.level_5_complaints = [...(level4Obj.level_5_complaints || []), { name: newLevel5Complaint.trim(), associated_medications: [], level_6_complaints: [] }];
                                  level4Array[managingLevel5ForIndex] = level4Obj;
                                  subSubObj.level_4_complaints = level4Array;
                                  subSubArray[managingLevel4ForIndex] = subSubObj;
                                  subObj.sub_sub_complaints = subSubArray;
                                  updatedSubComplaints[managingLevel4ForSubIndex] = subObj;
                                  return { ...prev, sub_complaints: updatedSubComplaints };
                                });
                                setNewLevel5Complaint('');
                              }
                            }
                          }}
                        />
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => {
                            if (newLevel5Complaint.trim()) {
                              setChiefComplaintForm(prev => {
                                const updatedSubComplaints = [...prev.sub_complaints];
                                const currentSub = updatedSubComplaints[managingLevel4ForSubIndex];
                                const subObj = typeof currentSub === 'string' ? { name: currentSub, associated_medications: [], sub_sub_complaints: [] } : { ...currentSub };
                                const subSubArray = [...(subObj.sub_sub_complaints || [])];
                                const currentSubSub = subSubArray[managingLevel4ForIndex];
                                const subSubObj = typeof currentSubSub === 'string' ? { name: currentSubSub, associated_medications: [], level_4_complaints: [] } : { ...currentSubSub };
                                const level4Array = [...(subSubObj.level_4_complaints || [])];
                                const currentLevel4 = level4Array[managingLevel5ForIndex];
                                const level4Obj = typeof currentLevel4 === 'string' ? { name: currentLevel4, associated_medications: [], level_5_complaints: [] } : { ...currentLevel4 };
                                
                                level4Obj.level_5_complaints = [...(level4Obj.level_5_complaints || []), { name: newLevel5Complaint.trim(), associated_medications: [], level_6_complaints: [] }];
                                level4Array[managingLevel5ForIndex] = level4Obj;
                                subSubObj.level_4_complaints = level4Array;
                                subSubArray[managingLevel4ForIndex] = subSubObj;
                                subObj.sub_sub_complaints = subSubArray;
                                updatedSubComplaints[managingLevel4ForSubIndex] = subObj;
                                return { ...prev, sub_complaints: updatedSubComplaints };
                              });
                              setNewLevel5Complaint('');
                            }
                          }}
                        >
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </>
                );
              })()}
              
              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button
                  type="button"
                  onClick={() => {
                    // Close all parent levels at once for better UX
                    setManagingLevel4ForIndex(null);
                    setManagingLevel4ForSubIndex(null);
                    setManagingLevel5ForIndex(null);
                    setManagingLevel6ForIndex(null);
                    setNewLevel4Complaint('');
                    setNewLevel5Complaint('');
                    setNewLevel6Complaint('');
                  }}
                >
                  Done
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      )}

      {/* Level 5 Complaint Medications Dialog - Conditionally rendered for performance */}
      {showLevel5Medications && (
      <Dialog open={true} onOpenChange={setShowLevel5Medications}>
        <DialogContent className="max-w-2xl max-h-[600px] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Add Medications for Level 5 Complaint
            </DialogTitle>
          </DialogHeader>
          
          {managingLevel5ForIndex !== null && editingLevel5Index !== null && (
            <div className="space-y-4">
              <div>
                <Label>Search and Add Medications</Label>
                <Input
                  placeholder="Search medications..."
                  value={chiefComplaintForm.medicationSearch}
                  onChange={(e) => setChiefComplaintForm(prev => ({ ...prev, medicationSearch: e.target.value }))}
                />
              </div>

              <div className="max-h-60 overflow-y-auto border rounded p-2 space-y-1">
                {managingLevel4ForSubIndex !== null && managingLevel4ForIndex !== null && managingLevel5ForIndex !== null && editingLevel5Index !== null && (
                  <>
                    {(() => {
                      const currentSubComplaint = chiefComplaintForm.sub_complaints[managingLevel4ForSubIndex];
                      const subSubComplaints = typeof currentSubComplaint === 'object' ? currentSubComplaint.sub_sub_complaints || [] : [];
                      const currentSubSub = subSubComplaints[managingLevel4ForIndex];
                      const level4Complaints = typeof currentSubSub === 'object' ? currentSubSub.level_4_complaints || [] : [];
                      const currentLevel4 = level4Complaints[managingLevel5ForIndex];
                      const level5Complaints = typeof currentLevel4 === 'object' ? currentLevel4.level_5_complaints || [] : [];
                      const currentLevel5 = level5Complaints[editingLevel5Index];
                      const level5Meds = typeof currentLevel5 === 'object' ? currentLevel5.associated_medications || [] : [];
                      
                      return [...medicationTemplates, ...commonMedications.map(m => ({ ...m, isCommon: true }))]
                        .filter(m => {
                          const name = m.medication_name || m.name || '';
                          return name.toLowerCase().includes((chiefComplaintForm.medicationSearch || '').toLowerCase());
                        })
                        .map(med => {
                          const medId = med.isCommon ? `common-${med.id}` : med.id;
                          const medName = med.medication_name || med.name;
                          const isSelected = level5Meds.includes(medId);
                    
                    return (
                      <button
                        key={medId}
                        type="button"
                        onClick={() => {
                          setChiefComplaintForm(prev => {
                            const updatedSubComplaints = [...prev.sub_complaints];
                            const currentSub = updatedSubComplaints[managingLevel4ForSubIndex];
                            const subObj = typeof currentSub === 'string' ? { name: currentSub, associated_medications: [], sub_sub_complaints: [] } : { ...currentSub };
                            const subSubArray = [...(subObj.sub_sub_complaints || [])];
                            const currentSubSub = subSubArray[managingLevel4ForIndex];
                            const subSubObj = typeof currentSubSub === 'string' ? { name: currentSubSub, associated_medications: [], level_4_complaints: [] } : { ...currentSubSub };
                            const level4Array = [...(subSubObj.level_4_complaints || [])];
                            const currentLevel4 = level4Array[managingLevel5ForIndex];
                            const level4Obj = typeof currentLevel4 === 'string' ? { name: currentLevel4, associated_medications: [], level_5_complaints: [] } : { ...currentLevel4 };
                            const level5Array = [...(level4Obj.level_5_complaints || [])];
                            const currentLevel5 = level5Array[editingLevel5Index];
                            const level5Obj = typeof currentLevel5 === 'string' ? { name: currentLevel5, associated_medications: [] } : { ...currentLevel5 };
                            
                            if (isSelected) {
                              level5Obj.associated_medications = level5Obj.associated_medications.filter(id => id !== medId);
                            } else {
                              level5Obj.associated_medications = [...(level5Obj.associated_medications || []), medId];
                            }
                            
                            level5Array[editingLevel5Index] = level5Obj;
                            level4Obj.level_5_complaints = level5Array;
                            level4Array[managingLevel5ForIndex] = level4Obj;
                            subSubObj.level_4_complaints = level4Array;
                            subSubArray[managingLevel4ForIndex] = subSubObj;
                            subObj.sub_sub_complaints = subSubArray;
                            updatedSubComplaints[managingLevel4ForSubIndex] = subObj;
                            return { ...prev, sub_complaints: updatedSubComplaints };
                          });
                        }}
                        className={`w-full text-left p-2 rounded border transition-colors ${
                          isSelected ? 'bg-blue-50 border-blue-500' : 'bg-white border-gray-200 hover:border-blue-300'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <div className={`w-4 h-4 rounded border flex items-center justify-center ${
                            isSelected ? 'bg-blue-500 border-blue-500' : 'border-gray-300'
                          }`}>
                            {isSelected && <span className="text-white text-xs">✓</span>}
                          </div>
                          <span className="text-sm">{medName}</span>
                          {med.isCommon && <span className="text-xs text-gray-500">(Common)</span>}
                        </div>
                      </button>
                    );
                        });
                    })()}
                  </>
                )}
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowLevel5Medications(false);
                    setEditingLevel5Index(null);
                  }}
                >
                  Done
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      )}

      {/* Level 6 Complaints Management Dialog - Conditionally rendered for performance */}
      {managingLevel6ForIndex !== null && (
      <Dialog open={true} onOpenChange={(open) => {
        if (!open) {
          setManagingLevel6ForIndex(null);
          setNewLevel6Complaint('');
          setEditingLevel6Index(null);
          setShowLevel6Medications(false);
        }
      }}>
        <DialogContent className="max-w-3xl max-h-[700px] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Manage Level 6 Complaints (Final Level)
            </DialogTitle>
          </DialogHeader>
          
          {managingLevel6ForIndex !== null && (
            <div className="space-y-4">
              {(() => {
                if (managingLevel4ForSubIndex === null || managingLevel4ForIndex === null || managingLevel5ForIndex === null || managingLevel6ForIndex === null) return null;
                
                const currentSubComplaint = chiefComplaintForm.sub_complaints[managingLevel4ForSubIndex];
                const subSubComplaints = typeof currentSubComplaint === 'object' ? currentSubComplaint.sub_sub_complaints || [] : [];
                const currentSubSub = subSubComplaints[managingLevel4ForIndex];
                const level4Complaints = typeof currentSubSub === 'object' ? currentSubSub.level_4_complaints || [] : [];
                const currentLevel4 = level4Complaints[managingLevel5ForIndex];
                const level5Complaints = typeof currentLevel4 === 'object' ? currentLevel4.level_5_complaints || [] : [];
                const currentLevel5 = level5Complaints[managingLevel6ForIndex];
                const level6Complaints = typeof currentLevel5 === 'object' ? currentLevel5.level_6_complaints || [] : [];
                
                return (
                  <>
                    {level6Complaints.length > 0 && (
                      <div className="space-y-2 mb-4">
                        <Label>Existing Level 6 Complaints</Label>
                        {level6Complaints.map((level6Complaint, index) => {
                          const level6Name = typeof level6Complaint === 'string' ? level6Complaint : level6Complaint.name;
                          const level6Meds = typeof level6Complaint === 'object' ? level6Complaint.associated_medications || [] : [];
                          
                          return (
                            <div key={index} className="p-3 bg-pink-50 border border-pink-200 rounded">
                              <div className="flex items-start gap-2">
                                <span className="text-sm flex-1 font-medium text-gray-900">{level6Name}</span>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setEditingLevel6Index(index);
                                    setShowLevel6Medications(true);
                                  }}
                                  className="text-blue-600 hover:text-blue-800 text-xs"
                                >
                                  + Meds
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setChiefComplaintForm(prev => {
                                      const updatedSubComplaints = [...prev.sub_complaints];
                                      const currentSub = updatedSubComplaints[managingLevel4ForSubIndex];
                                      const subObj = typeof currentSub === 'string' ? { name: currentSub, associated_medications: [], sub_sub_complaints: [] } : { ...currentSub };
                                      const subSubArray = [...(subObj.sub_sub_complaints || [])];
                                      const currentSubSub = subSubArray[managingLevel4ForIndex];
                                      const subSubObj = typeof currentSubSub === 'string' ? { name: currentSubSub, associated_medications: [], level_4_complaints: [] } : { ...currentSubSub };
                                      const level4Array = [...(subSubObj.level_4_complaints || [])];
                                      const currentLevel4 = level4Array[managingLevel5ForIndex];
                                      const level4Obj = typeof currentLevel4 === 'string' ? { name: currentLevel4, associated_medications: [], level_5_complaints: [] } : { ...currentLevel4 };
                                      const level5Array = [...(level4Obj.level_5_complaints || [])];
                                      const currentLevel5 = level5Array[managingLevel6ForIndex];
                                      const level5Obj = typeof currentLevel5 === 'string' ? { name: currentLevel5, associated_medications: [], level_6_complaints: [] } : { ...currentLevel5 };
                                      
                                      level5Obj.level_6_complaints = (level5Obj.level_6_complaints || []).filter((_, i) => i !== index);
                                      level5Array[managingLevel6ForIndex] = level5Obj;
                                      level4Obj.level_5_complaints = level5Array;
                                      level4Array[managingLevel5ForIndex] = level4Obj;
                                      subSubObj.level_4_complaints = level4Array;
                                      subSubArray[managingLevel4ForIndex] = subSubObj;
                                      subObj.sub_sub_complaints = subSubArray;
                                      updatedSubComplaints[managingLevel4ForSubIndex] = subObj;
                                      return { ...prev, sub_complaints: updatedSubComplaints };
                                    });
                                  }}
                                  className="text-red-600 hover:text-red-800"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              </div>
                              {level6Meds && level6Meds.length > 0 && (
                                <div className="mt-2 text-xs text-gray-600">
                                  <span className="font-medium">Medications:</span> {level6Meds.length}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                    
                    <div>
                      <Label>Add New Level 6 Complaint (Deepest Level)</Label>
                      <div className="flex gap-2 mt-2">
                        <Input
                          placeholder="e.g., Severe, Mild"
                          value={newLevel6Complaint}
                          onChange={(e) => setNewLevel6Complaint(e.target.value)}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              if (newLevel6Complaint.trim()) {
                                setChiefComplaintForm(prev => {
                                  const updatedSubComplaints = [...prev.sub_complaints];
                                  const currentSub = updatedSubComplaints[managingLevel4ForSubIndex];
                                  const subObj = typeof currentSub === 'string' ? { name: currentSub, associated_medications: [], sub_sub_complaints: [] } : { ...currentSub };
                                  const subSubArray = [...(subObj.sub_sub_complaints || [])];
                                  const currentSubSub = subSubArray[managingLevel4ForIndex];
                                  const subSubObj = typeof currentSubSub === 'string' ? { name: currentSubSub, associated_medications: [], level_4_complaints: [] } : { ...currentSubSub };
                                  const level4Array = [...(subSubObj.level_4_complaints || [])];
                                  const currentLevel4 = level4Array[managingLevel5ForIndex];
                                  const level4Obj = typeof currentLevel4 === 'string' ? { name: currentLevel4, associated_medications: [], level_5_complaints: [] } : { ...currentLevel4 };
                                  const level5Array = [...(level4Obj.level_5_complaints || [])];
                                  const currentLevel5 = level5Array[managingLevel6ForIndex];
                                  const level5Obj = typeof currentLevel5 === 'string' ? { name: currentLevel5, associated_medications: [], level_6_complaints: [] } : { ...currentLevel5 };
                                  
                                  level5Obj.level_6_complaints = [...(level5Obj.level_6_complaints || []), { name: newLevel6Complaint.trim(), associated_medications: [] }];
                                  level5Array[managingLevel6ForIndex] = level5Obj;
                                  level4Obj.level_5_complaints = level5Array;
                                  level4Array[managingLevel5ForIndex] = level4Obj;
                                  subSubObj.level_4_complaints = level4Array;
                                  subSubArray[managingLevel4ForIndex] = subSubObj;
                                  subObj.sub_sub_complaints = subSubArray;
                                  updatedSubComplaints[managingLevel4ForSubIndex] = subObj;
                                  return { ...prev, sub_complaints: updatedSubComplaints };
                                });
                                setNewLevel6Complaint('');
                              }
                            }
                          }}
                        />
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => {
                            if (newLevel6Complaint.trim()) {
                              setChiefComplaintForm(prev => {
                                const updatedSubComplaints = [...prev.sub_complaints];
                                const currentSub = updatedSubComplaints[managingLevel4ForSubIndex];
                                const subObj = typeof currentSub === 'string' ? { name: currentSub, associated_medications: [], sub_sub_complaints: [] } : { ...currentSub };
                                const subSubArray = [...(subObj.sub_sub_complaints || [])];
                                const currentSubSub = subSubArray[managingLevel4ForIndex];
                                const subSubObj = typeof currentSubSub === 'string' ? { name: currentSubSub, associated_medications: [], level_4_complaints: [] } : { ...currentSubSub };
                                const level4Array = [...(subSubObj.level_4_complaints || [])];
                                const currentLevel4 = level4Array[managingLevel5ForIndex];
                                const level4Obj = typeof currentLevel4 === 'string' ? { name: currentLevel4, associated_medications: [], level_5_complaints: [] } : { ...currentLevel4 };
                                const level5Array = [...(level4Obj.level_5_complaints || [])];
                                const currentLevel5 = level5Array[managingLevel6ForIndex];
                                const level5Obj = typeof currentLevel5 === 'string' ? { name: currentLevel5, associated_medications: [], level_6_complaints: [] } : { ...currentLevel5 };
                                
                                level5Obj.level_6_complaints = [...(level5Obj.level_6_complaints || []), { name: newLevel6Complaint.trim(), associated_medications: [] }];
                                level5Array[managingLevel6ForIndex] = level5Obj;
                                level4Obj.level_5_complaints = level5Array;
                                level4Array[managingLevel5ForIndex] = level4Obj;
                                subSubObj.level_4_complaints = level4Array;
                                subSubArray[managingLevel4ForIndex] = subSubObj;
                                subObj.sub_sub_complaints = subSubArray;
                                updatedSubComplaints[managingLevel4ForSubIndex] = subObj;
                                return { ...prev, sub_complaints: updatedSubComplaints };
                              });
                              setNewLevel6Complaint('');
                            }
                          }}
                        >
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </>
                );
              })()}
              
              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button
                  type="button"
                  onClick={() => {
                    // Close all parent levels at once for better UX
                    setManagingLevel4ForIndex(null);
                    setManagingLevel4ForSubIndex(null);
                    setManagingLevel5ForIndex(null);
                    setManagingLevel6ForIndex(null);
                    setNewLevel4Complaint('');
                    setNewLevel5Complaint('');
                    setNewLevel6Complaint('');
                  }}
                >
                  Done
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      )}

      {/* Level 6 Complaint Medications Dialog - Conditionally rendered for performance */}
      {showLevel6Medications && (
      <Dialog open={true} onOpenChange={setShowLevel6Medications}>
        <DialogContent className="max-w-2xl max-h-[600px] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Add Medications for Level 6 Complaint (Final Level)
            </DialogTitle>
          </DialogHeader>
          
          {managingLevel6ForIndex !== null && editingLevel6Index !== null && (
            <div className="space-y-4">
              <div>
                <Label>Search and Add Medications</Label>
                <Input
                  placeholder="Search medications..."
                  value={chiefComplaintForm.medicationSearch}
                  onChange={(e) => setChiefComplaintForm(prev => ({ ...prev, medicationSearch: e.target.value }))}
                />
              </div>

              <div className="max-h-60 overflow-y-auto border rounded p-2 space-y-1">
                {managingLevel4ForSubIndex !== null && managingLevel4ForIndex !== null && managingLevel5ForIndex !== null && managingLevel6ForIndex !== null && editingLevel6Index !== null && (
                  <>
                    {(() => {
                      const currentSubComplaint = chiefComplaintForm.sub_complaints[managingLevel4ForSubIndex];
                      const subSubComplaints = typeof currentSubComplaint === 'object' ? currentSubComplaint.sub_sub_complaints || [] : [];
                      const currentSubSub = subSubComplaints[managingLevel4ForIndex];
                      const level4Complaints = typeof currentSubSub === 'object' ? currentSubSub.level_4_complaints || [] : [];
                      const currentLevel4 = level4Complaints[managingLevel5ForIndex];
                      const level5Complaints = typeof currentLevel4 === 'object' ? currentLevel4.level_5_complaints || [] : [];
                      const currentLevel5 = level5Complaints[managingLevel6ForIndex];
                      const level6Complaints = typeof currentLevel5 === 'object' ? currentLevel5.level_6_complaints || [] : [];
                      const currentLevel6 = level6Complaints[editingLevel6Index];
                      const level6Meds = typeof currentLevel6 === 'object' ? currentLevel6.associated_medications || [] : [];
                      
                      return [...medicationTemplates, ...commonMedications.map(m => ({ ...m, isCommon: true }))]
                        .filter(m => {
                          const name = m.medication_name || m.name || '';
                          return name.toLowerCase().includes((chiefComplaintForm.medicationSearch || '').toLowerCase());
                        })
                        .map(med => {
                          const medId = med.isCommon ? `common-${med.id}` : med.id;
                          const medName = med.medication_name || med.name;
                          const isSelected = level6Meds.includes(medId);
                    
                    return (
                      <button
                        key={medId}
                        type="button"
                        onClick={() => {
                          setChiefComplaintForm(prev => {
                            const updatedSubComplaints = [...prev.sub_complaints];
                            const currentSub = updatedSubComplaints[managingLevel4ForSubIndex];
                            const subObj = typeof currentSub === 'string' ? { name: currentSub, associated_medications: [], sub_sub_complaints: [] } : { ...currentSub };
                            const subSubArray = [...(subObj.sub_sub_complaints || [])];
                            const currentSubSub = subSubArray[managingLevel4ForIndex];
                            const subSubObj = typeof currentSubSub === 'string' ? { name: currentSubSub, associated_medications: [], level_4_complaints: [] } : { ...currentSubSub };
                            const level4Array = [...(subSubObj.level_4_complaints || [])];
                            const currentLevel4 = level4Array[managingLevel5ForIndex];
                            const level4Obj = typeof currentLevel4 === 'string' ? { name: currentLevel4, associated_medications: [], level_5_complaints: [] } : { ...currentLevel4 };
                            const level5Array = [...(level4Obj.level_5_complaints || [])];
                            const currentLevel5 = level5Array[managingLevel6ForIndex];
                            const level5Obj = typeof currentLevel5 === 'string' ? { name: currentLevel5, associated_medications: [], level_6_complaints: [] } : { ...currentLevel5 };
                            const level6Array = [...(level5Obj.level_6_complaints || [])];
                            const currentLevel6 = level6Array[editingLevel6Index];
                            const level6Obj = typeof currentLevel6 === 'string' ? { name: currentLevel6, associated_medications: [] } : { ...currentLevel6 };
                            
                            if (isSelected) {
                              level6Obj.associated_medications = level6Obj.associated_medications.filter(id => id !== medId);
                            } else {
                              level6Obj.associated_medications = [...(level6Obj.associated_medications || []), medId];
                            }
                            
                            level6Array[editingLevel6Index] = level6Obj;
                            level5Obj.level_6_complaints = level6Array;
                            level5Array[managingLevel6ForIndex] = level5Obj;
                            level4Obj.level_5_complaints = level5Array;
                            level4Array[managingLevel5ForIndex] = level4Obj;
                            subSubObj.level_4_complaints = level4Array;
                            subSubArray[managingLevel4ForIndex] = subSubObj;
                            subObj.sub_sub_complaints = subSubArray;
                            updatedSubComplaints[managingLevel4ForSubIndex] = subObj;
                            return { ...prev, sub_complaints: updatedSubComplaints };
                          });
                        }}
                        className={`w-full text-left p-2 rounded border transition-colors ${
                          isSelected ? 'bg-blue-50 border-blue-500' : 'bg-white border-gray-200 hover:border-blue-300'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <div className={`w-4 h-4 rounded border flex items-center justify-center ${
                            isSelected ? 'bg-blue-500 border-blue-500' : 'border-gray-300'
                          }`}>
                            {isSelected && <span className="text-white text-xs">✓</span>}
                          </div>
                          <span className="text-sm">{medName}</span>
                          {med.isCommon && <span className="text-xs text-gray-500">(Common)</span>}
                        </div>
                      </button>
                    );
                        });
                    })()}
                  </>
                )}
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowLevel6Medications(false);
                    setEditingLevel6Index(null);
                  }}
                >
                  Done
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      )}

      {/* Add/Edit Additional Notes Dialog - Conditionally rendered for performance */}
      {(showAddAdditionalNotes || showEditAdditionalNotes) && (
        <Dialog open={true} onOpenChange={(open) => {
          if (!open) {
            setShowAddAdditionalNotes(false);
            setShowEditAdditionalNotes(false);
            setEditingAdditionalNotes(null);
          }
        }}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingAdditionalNotes ? 'Edit Advice' : 'Add Advice'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={editingAdditionalNotes ? handleEditAdditionalNotes : handleAddAdditionalNotes} className="space-y-4">
              <div>
                <Label>Note Title *</Label>
                <Input
                  placeholder="e.g., Follow-up Instructions, Dietary Advice"
                  value={additionalNotesForm.note_title}
                  onChange={(e) => setAdditionalNotesForm(prev => ({ ...prev, note_title: e.target.value }))}
                  required
                  autoFocus
                />
              </div>
              
              <div>
                <Label>Note Content (Optional)</Label>
                <Textarea
                  placeholder="Enter the note content..."
                  value={additionalNotesForm.note_content}
                  onChange={(e) => setAdditionalNotesForm(prev => ({ ...prev, note_content: e.target.value }))}
                  rows={4}
                />
              </div>
              
              <div className="flex space-x-2 pt-4">
                <Button type="submit" className="flex-1 bg-teal-600 hover:bg-teal-700">Save Template</Button>
                <Button type="button" variant="outline" onClick={() => {
                  setShowAddAdditionalNotes(false);
                  setShowEditAdditionalNotes(false);
                  setEditingAdditionalNotes(null);
                }} className="flex-1">Cancel</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      )}


      {/* Bulk Import Dialog - Conditionally rendered for performance */}
      {showBulkImport && (
      <Dialog open={true} onOpenChange={(open) => {
        setShowBulkImport(open);
        if (!open) {
          setBulkImportText('');
          setCsvFile(null);
          setUploadMethod('paste');
        }
      }}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Bulk Import Medications</DialogTitle>
          </DialogHeader>
          
          {/* Upload Method Tabs */}
          <div className="flex space-x-2 mb-4 border-b">
            <button
              onClick={() => setUploadMethod('paste')}
              className={`px-4 py-2 font-medium text-sm transition-colors ${
                uploadMethod === 'paste'
                  ? 'border-b-2 border-blue-600 text-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Paste Data
            </button>
            <button
              onClick={() => setUploadMethod('csv')}
              className={`px-4 py-2 font-medium text-sm transition-colors ${
                uploadMethod === 'csv'
                  ? 'border-b-2 border-blue-600 text-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Upload CSV
            </button>
          </div>

          <div className="space-y-4">
            {/* Paste Method */}
            {uploadMethod === 'paste' && (
              <>
                <div>
                  <Label>Paste Medication Data</Label>
                  <p className="text-xs text-gray-500 mb-2">
                    Format: medication_name,timing,duration,time_of_day|separated,notes
                    <br />
                    Example: Paracetamol 500mg,After Food,7 days,Morning|Evening,Take with water
                  </p>
                  <Textarea
                    placeholder="Paste your medication data here (one per line)..."
                    value={bulkImportText}
                    onChange={(e) => setBulkImportText(e.target.value)}
                    rows={10}
                    className="font-mono text-sm"
                  />
                </div>
                <div className="flex space-x-2">
                  <Button onClick={handleBulkImport} className="flex-1 bg-blue-600 hover:bg-blue-700">
                    <Upload className="w-4 h-4 mr-2" />
                    Import
                  </Button>
                  <Button variant="outline" onClick={() => setShowBulkImport(false)} className="flex-1">Cancel</Button>
                </div>
              </>
            )}

            {/* CSV Upload Method */}
            {uploadMethod === 'csv' && (
              <>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-800">
                    <strong>Upload CSV File:</strong> Import medication templates from a CSV file
                  </p>
                </div>

                {/* Download Template Button */}
                <div>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      const csvContent = `brand_name,generic_name,medication_type,category,treatment_for
Crocin 650mg,Paracetamol,tablet,Analgesic,Fever and Pain Relief
Augmentin 625mg,Amoxicillin + Clavulanic Acid,tablet,Antibiotic,Bacterial Infections
Allegra 120mg,Fexofenadine,tablet,Antihistamine,Allergic Rhinitis
Metformin 500mg,Metformin,tablet,Antidiabetic,Type 2 Diabetes
Pantop 40mg,Pantoprazole,tablet,Proton Pump Inhibitor,Acidity and GERD`;
                      
                      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
                      const link = document.createElement('a');
                      link.href = URL.createObjectURL(blob);
                      link.download = 'medication_templates.csv';
                      link.click();
                    }}
                    className="w-full"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download Sample CSV Template
                  </Button>
                </div>

                <div>
                  <Label className="text-sm font-medium">Required CSV Columns (Header Row)</Label>
                  <div className="mt-2 bg-gray-50 border border-gray-200 rounded p-3">
                    <p className="text-xs font-mono text-gray-700 break-all">
                      brand_name,generic_name,medication_type,category,treatment_for
                    </p>
                  </div>
                  <ul className="text-xs text-gray-600 mt-2 space-y-1 list-disc list-inside">
                    <li><strong className="text-red-600">brand_name</strong>: Required (e.g., "Crocin 650mg")</li>
                    <li><strong className="text-red-600">generic_name</strong>: Required (e.g., "Paracetamol")</li>
                    <li><strong>medication_type</strong>: Optional (tablet/capsule/syrup/injection)</li>
                    <li><strong>category</strong>: Optional (e.g., "Analgesic", "Antibiotic")</li>
                    <li><strong>treatment_for</strong>: Optional (e.g., "Fever and Pain Relief")</li>
                  </ul>
                </div>

                <div>
                  <Label className="text-sm font-medium">Select CSV File</Label>
                  <Input
                    type="file"
                    accept=".csv"
                    onChange={(e) => setCsvFile(e.target.files[0])}
                    className="mt-2"
                  />
                  {csvFile && (
                    <p className="text-sm text-green-600 mt-2 flex items-center">
                      <span className="mr-2">✓</span> Selected: {csvFile.name}
                    </p>
                  )}
                </div>

                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <p className="text-xs text-yellow-800">
                    <strong>💡 Tips:</strong>
                  </p>
                  <ul className="text-xs text-yellow-700 mt-1 space-y-1 list-disc list-inside">
                    <li>Save Excel file as "CSV UTF-8 (Comma delimited) (*.csv)"</li>
                    <li>First row must be the header: brand_name,generic_name,medication_type,category,treatment_for</li>
                    <li><strong>Both brand_name and generic_name are mandatory</strong></li>
                    <li>Duplicate brand names will be skipped automatically</li>
                    <li>Leave optional fields empty if not needed</li>
                  </ul>
                </div>

                <div className="flex space-x-2 pt-4 border-t">
                  <Button 
                    onClick={handleCSVUpload}
                    className="flex-1 bg-blue-600 hover:bg-blue-700"
                    disabled={!csvFile}
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Import from CSV
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={() => setShowBulkImport(false)} 
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
      )}
    </div>
  );
};

export default ConfigurationTab;
